/*
 * Vitrine — Main controller. Wires data → painter → viewer → DOM.
 *
 * Sections (search to navigate):
 *   STATE         — single state object
 *   BOOT          — preload assets, then reveal page
 *   PROGRESS      — top progress strip
 *   NAVIGATE      — step transitions (slide, back-to-review)
 *   STEP DESIGN   — shoe grid, category filters
 *   STEP COLORS   — colorway grid, category filters, hover preview disabled
 *   STEP LINING   — three cards
 *   STEP SOLE     — slider over cross-section diagram
 *   STEP REVIEW   — 3D viewer, summary, action chips
 *   PARTS EDITOR  — modal with parts list + swatches; simple/advanced; texture/functional
 *   CART          — add to cart + name modal + login prompt
 */
(function (w) {
  'use strict';

  var D = w.VitrineData;
  var P = w.VitrinePainter;
  var V = w.VitrineViewer;
  // Build model + colour card shared with the product page (cf-vitrine-kit.js).
  var K = w.VitrineKit;
  if (!D || !P || !V) { console.error('[vitrine] missing module'); return; }

  // ── STEP FLOW ─────────────────────────────────────────────────────────────
  // The linear flow is owned here in the theme so the parts editor can sit in the
  // main sequence as "Customize" — the shared data bundle still lists `parts`
  // last + hidden. Order: Design → Colors → Sole → Customize → Review. Materials
  // and Lining are no longer their own steps — material is the shoe's detected mix
  // (refined per-part in Customize) and lining lives inside Customize as the last
  // part before Settings (see the lining sub-panel in renderPartsStep). The
  // internal slug stays 'parts' (DOM panel, canvas list, onEnterStep all key off
  // it); only the user-facing label changes. Eyebrows are renumbered from this
  // order so "04 — Customize", "05 — Review" stay in sync.
  //
  // ?beta=true → the one-page Vitrine (assets/cf-vitrine-beta.js): Design, then
  // a single "build" panel with every setting beside the live shoe. Customize
  // ('parts') stays reachable from it as a sub-page; the other steps' panels are
  // still rendered (their helpers keep state in sync) but never navigated to.
  // 2026-10-31: the one-page build is the only Vitrine (michael: "remove the old
  // vitrine - this is the only way"). The old step flow below is kept only as
  // the shared helpers the build drives; BETA stays as the switch name.
  var BETA = true;
  var STEP_FLOW = BETA ? ['design', 'build'] : ['design', 'colors', 'sole', 'parts', 'review'];
  var STEP_TITLE_OVERRIDE = { parts: 'Customize', build: 'Your shoe' };
  var STEP_BLURB_OVERRIDE = {
    parts:  'Refine each part — its colour and material.',
    review: 'Spin it. Look it over. Send it.',
    build:  'Every setting, in one place.'
  };
  var STEPS = STEP_FLOW.map(function (slug, i) {
    var base  = D.steps.find(function (s) { return s.slug === slug; }) || {};
    var index = i + 1;
    var title = STEP_TITLE_OVERRIDE[slug] || base.title || slug;
    return {
      slug:    slug,
      index:   index,
      title:   title,
      eyebrow: String(index).padStart(2, '0') + ' — ' + title,
      blurb:   STEP_BLURB_OVERRIDE[slug] || base.blurb || ''
    };
  });
  function stepIndex(slug) { return STEPS.findIndex(function (s) { return s.slug === slug; }); }

  // ── STATE ─────────────────────────────────────────────────────────────────
  var state = {
    currentStep:    'design',
    reachedReview:  false,           // once Review has been seen, every earlier step's primary button jumps back to it
    skipWelcome:    false,           // true when restoring from URL — go straight to review
    shoeFilter:     'all',
    selectedShoe:   D.shoes[0],
    shoeGender:     'male',
    colorwayFilter: 'all',
    selectedColorway: D.initialColorway,
    selectedLining: D.liningOptions.find(function (l) { return l.id === 'leather'; }) || D.liningOptions[0],
    selectedSoleIdx: 1,              // index into D.soleStackPositions
    welt:           true,            // false = built without a welt (and its stitching); same price
    partColors:     {},              // groupId → palette entry (overrides colorway)
    partColorsByMeshName: {},        // meshName → palette entry (stable across mode switches)
    partMaterials:  {},              // groupId → 'leather' | 'suede'
    partMaterialsByMeshName: {},     // meshName → 'leather' | 'suede' (stable across mode switches)
    materialMode:   'mixed',         // 'mixed' (detected split) | 'leather' | 'suede' — default mix; refined per-part in Customize
    partsMode:      'simple',        // 'simple' | 'advanced'
    partsGrouping:  'texture',       // 'texture' | 'functional' — toggled in cog
    notes:          '',
    name:           '',
    editingLineKey: null,            // cart line key when reopened from a cart "Edit" link
    editLineInCart: false,           // whether that line still exists in the cart
    addedToCart:    false            // true once this build has been added — promotes "Take photos" to primary
  };

  // Cached preloaded GLTF objects so re-renders are instant. `referenceImageBytes`
  // keeps the raw image bytes (collected during preload + auto-detection) so we
  // can build our OWN THREE.Texture instances for the Leather/Suede swap —
  // independent of GLTFLoader's image pipeline, which occasionally fails to
  // decode GLB-embedded blobs when mimeType is missing in the JSON.
  var preload = {
    models: {}, modelsFemale: {}, soles: {}, solesFemale: {},
    modelBuffers: {}, modelBuffersFemale: {},            // shoeId → ArrayBuffer (reused at live-scene load)
    shoeMd5ToKind: {}, shoeMd5ToKindFemale: {},         // shoeId → { md5: kindId } for each gender set
    referenceTextures:   { leather: null, suede: null },
    referenceMaterials:  {}                              // kind → snapshot of material props (shine, normalMap, …)
  };

  // Cached thumbnail data URLs so re-rendering a grid (e.g. on filter click)
  // doesn't drop the images.
  var thumbCache = { shoe: {}, cw: {} };

  // 1x1 transparent SVG used as a placeholder src so empty <img> tags never
  // trigger phantom network requests (browsers fetch the page URL for empty src).
  var PLACEHOLDER_IMG = "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'/%3E";

  // Display name overrides — transform part labels for UI display only.
  // Preserves case: "Aiglet" → "Eyestay", "aiglet" → "eyestay", etc.

  function displayLabel(label) { return K.displayLabel(label); }

  // Three.js viewer is created once preload finishes — it owns both the live canvas
  // and the offscreen thumbnail renderer.
  var viewer = null;
  var beta = null;     // ?beta=true UI (VitrineBeta.create) — null in the normal flow
  var liveGroups = []; // current materialGroups in the live viewer (review step)
  var loadedShoeGender = null;

  // Design-step coverflow carousel (shares the viewer's renderer). currentDesignShoes
  // is the filtered list it's showing; designEntryShoeId tracks the shoe we entered
  // the step on, so part overrides only reset when the base shape actually changes.
  var carousel = null;
  var currentDesignShoes = [];
  var designEntryShoeId = null;
  var designColorway = null;       // fallback / centred-shoe colourway
  var designColorways = {};        // shoeId → colourway shown on the carousel
  var designColorwayTimer = null;

  // Snapshot of the currently-loaded model's NATIVE body materials, captured
  // right after load+tag (before any swap). Two uses:
  //   kindSource[kind] — the shoe's own diffuse texture + props for that kind,
  //     used as the swap source so the scale/look matches this exact model
  //     (Club's reference is only a fallback when the shoe lacks that kind).
  //   snapshot[meshName] — per-mesh native map/props/kind, so "Mixed" can
  //     restore the detected split in place without reloading the GLB.
  var liveModel = { kindSource: {}, snapshot: {} };

  // ── DOM refs (set in init) ────────────────────────────────────────────────
  var rootEl, stageEl, progressEl;

  // ──────────────────────────────────────────────────────────────────────────
  // BOOT
  // ──────────────────────────────────────────────────────────────────────────
  function init() {
    rootEl     = document.getElementById('vt-root');
    stageEl    = document.getElementById('vt-stage');
    progressEl = document.getElementById('vt-progress');
    if (!rootEl || !stageEl || !progressEl) { console.error('[vitrine] root markup missing'); return; }

    // Downloads first — everything below (step markup, first layout) runs while
    // the GLBs are on the wire instead of before they're requested.
    kickPreload();

    // Single source of truth: D.materialKinds order. partKey reads this so
    // flipping the data file's order is the ONLY place colour/label mapping
    // is decided. Without it we'd have hardcoded prefixes drift out of sync.
    P.setKindPriority(D.materialKinds.map(function (mk) { return mk.id; }));
    if (P.setBodyPartAllowlist) P.setBodyPartAllowlist(partKindOverridePrefixes());

    if (BETA) setupBetaShell();
    setupHistoryNav();
    setupBrandLink();

    renderProgress();
    renderDesignStep();
    renderColorsStep();
    renderSoleStep();
    renderReviewStep();
    renderPartsStep();
    bindGlobalHandlers();
    if (BETA) startBeta();
    goToStep('design', { instant: true, noPush: true });

    // WebGL renderer, environment map and backdrop don't need the models — set
    // them up during the downloads. Nothing renders until the carousel exists.
    initViewer();
    if (viewer && state.currentStep === 'design') viewer.setMode('carousel');

    if (rootEl) rootEl.classList.remove('vt-booting');

    var params = new URLSearchParams(location.search);
    // ?cfg= is the one build param (same as the product page); ?r= / ?restore=
    // are older links and cart edit URLs, still read.
    var restoreToken = params.get('cfg') || params.get('r') || params.get('restore');
    var editLine     = params.get('line'); // cart line key when reopened from a cart "Edit" link
    // Preload runs immediately on page load; the loading screen covers it. Welcome
    // only appears once everything's ready, so users never see blank thumbnails.
    // ?viewas=booth → kiosk demo: fullscreen turntable cycling shoes + colourways
    // (see BOOTH MODE below). Takes precedence over ?cfg= and skips the welcome.
    var boothRequested = params.get('viewas') === 'booth';
    if (boothRequested) document.body.classList.add('vt-booth-mode');
    // Restore and booth go straight to a 3D view, so they build everything up
    // front; the normal path shows the welcome first and builds the Design
    // carousel behind it.
    runPreloadThenStart({ deferCarousel: !boothRequested && !restoreToken }, function () {
      if (boothRequested)    { startBooth(); }
      else if (restoreToken) { state.skipWelcome = true; state.editingLineKey = editLine || null; tryRestore(restoreToken); }
      else                   { showWelcome(); }
    });
  }

  function hideLoadingOverlay() {
    var ov = document.getElementById('vt-loading');
    if (ov) ov.classList.add('vt-hidden');
    if (rootEl) rootEl.classList.remove('vt-booting');
  }

  // Welcome — full-screen intro. Shown instantly; no GLBs preload until Begin.
  function showWelcome() {
    if (state.skipWelcome) return;
    var el = document.getElementById('vt-welcome');
    if (!el) return;
    el.classList.remove('vt-hidden');
    var ok = document.getElementById('vt-welcome-ok');
    if (ok) ok.addEventListener('click', onBegin, { once: true });
  }

  function onBegin() {
    // Begin click fades the welcome and reveals the (already-loaded) design step.
    var welcome = document.getElementById('vt-welcome');
    if (!welcome) return;
    welcome.classList.add('vt-leaving');
    setTimeout(function () { welcome.classList.add('vt-hidden'); welcome.classList.remove('vt-leaving'); }, 500);
  }

  // preloadAssets starts at the top of init(); runPreloadThenStart waits on it.
  var preloadGate = { done: false, waiters: [] };
  function kickPreload() {
    preloadAssets(function () {
      preloadGate.done = true;
      var ws = preloadGate.waiters; preloadGate.waiters = [];
      ws.forEach(function (f) { f(); });
    });
  }

  function runPreloadThenStart(opts, afterStart) {
    function go() {
      detectMaterialKinds();
      initViewer();
      // Colorway thumbnails are generated lazily — only when the user navigates
      // to step 2. Keeps the post-Begin work focused on what's actually visible.
      if (!opts.deferCarousel) initCarousel();
      hideLoadingOverlay();
      if (typeof afterStart === 'function') afterStart();
      // The carousel (clone + fit + paint every shoe) is the biggest block of
      // post-load work; on the normal path it's built after the welcome has
      // painted, while the customer reads it — not behind the loading screen.
      if (opts.deferCarousel) {
        requestAnimationFrame(function () { setTimeout(initCarousel, 0); });
      }
      // Female shapes download now, behind the welcome / first steps.
      setTimeout(loadFemaleSet, 0);
    }
    if (preloadGate.done) go(); else preloadGate.waiters.push(go);
  }

  // Preload the shoe + sole GLBs. Updates the loading bar as it goes.
  // For shoes we need the raw bytes too — we MD5 each embedded image to map
  // materialName → kind (the painter tags those materials so the parts editor
  // knows leather vs suede). To avoid downloading every shoe twice, we fetch the
  // bytes ONCE and parse those same bytes into the GLTF: loader.load(url) is just
  // fetch(url) → parse(data, urlBase) internally, so calling parse with the real
  // URL base is identical to load() (no empty-path blob-texture bug) but halves
  // the network requests. Soles go the same way (their bytes aren't kept).
  //
  // Speed (2026-10-28): every download starts at once and each file parses as
  // soon as it lands (Draco decodes on worker threads), instead of one
  // fetch → parse → next file at a time. And only the set the builder opens on
  // (male shapes + soles) holds the loading screen; the female set loads in the
  // background straight after — see loadFemaleSet() / whenGenderReady().
  function gltfLoader() {
    var loader = new THREE.GLTFLoader();
    // Shoe GLBs are Draco-compressed — attach the shared decoder or they won't load.
    if (w.__cfDracoLoader && loader.setDRACOLoader) loader.setDRACOLoader(w.__cfDracoLoader);
    return loader;
  }

  function preloadJobs(female) {
    var A = w.VitrineAssets || {};
    var shoeUrls = female ? (A.shoesFemale || {}) : null;
    var soleUrls = female ? (A.solesFemale || {}) : null;
    var shoes = D.shoes.filter(function (s) { return !!s.glb; }).map(function (s) {
      return { kind: female ? 'shoeFemale' : 'shoe', id: s.id, url: female ? (shoeUrls[s.id] || '') : shoeUrl(s.id) };
    });
    var soles = Object.keys(D.soleGlbs).map(function (k) {
      return { kind: female ? 'soleFemale' : 'sole', id: k, url: female ? (soleUrls[k] || '') : soleUrl(k) };
    });
    return shoes.concat(soles);
  }

  // Fetch + parse every job in parallel; onEach() after each one settles
  // (loaded or failed), done() once all have.
  function loadJobs(jobs, onEach, done) {
    var loader = gltfLoader();
    var left = jobs.length;
    if (!left) { done(); return; }
    jobs.forEach(function (j) {
      var isShoe = j.kind === 'shoe' || j.kind === 'shoeFemale';
      var settled = false;
      function finish(gltf, buf) {
        if (settled) return;
        settled = true;
        // Shoe id on the scene — clones (carousel, thumbnails) carry it, and the
        // viewer reads it for per-shoe sole-fit tuning (cfSoleFitOptions).
        if (gltf && isShoe && gltf.scene) gltf.scene.userData.cfShoeId = j.id;
        if (gltf) {
          if (j.kind === 'shoe') {
            preload.models[j.id] = gltf;
            if (buf) preload.modelBuffers[j.id] = buf;
          } else if (j.kind === 'shoeFemale') {
            preload.modelsFemale[j.id] = gltf;
            if (buf) preload.modelBuffersFemale[j.id] = buf;
          } else if (j.kind === 'sole') {
            preload.soles[j.id] = gltf;
          } else if (j.kind === 'soleFemale') {
            preload.solesFemale[j.id] = gltf;
          }
        }
        if (onEach) onEach();
        if (--left === 0) done();
      }
      if (!j.url) { finish(null); return; }
      fetch(j.url)
        .then(function (r) {
          if (!r.ok) throw new Error('GLB fetch ' + r.status + ' ' + j.url);
          return r.arrayBuffer();
        })
        .then(function (b) {
          loader.parse(b, THREE.LoaderUtils.extractUrlBase(j.url),
            function (g) { finish(g, isShoe ? b : null); },
            function (err) { console.error('[vitrine] GLB parse failed:', j.url, err); finish(null); });
        })
        .catch(function (err) { console.error('[vitrine] GLB load failed:', j.url, err); finish(null); });
    });
  }

  function preloadAssets(done) {
    var fill = document.getElementById('vt-loading-fill');
    var pct  = document.getElementById('vt-loading-pct');
    var msg  = document.getElementById('vt-loading-msg');
    var jobs = preloadJobs(false);
    if (!jobs.length) return done();

    // Rotating slogans while the GLBs download — deliberately never model or file
    // names. The %/bar still reflects real load progress.
    var SLOGANS = [
      'Cut to the shape of your foot.',
      'Two photos. One perfect fit.',
      'Built to your measurements — never a size.',
      'Handmade in Porto, just for you.',
      'No two pairs alike.'
    ];
    var sIdx = 0;
    if (msg) msg.textContent = SLOGANS[0];
    var sloganTimer = setInterval(function () {
      sIdx = (sIdx + 1) % SLOGANS.length;
      if (msg) msg.textContent = SLOGANS[sIdx];
    }, 1500);

    var settledCount = 0;
    function progress() {
      var p = Math.round((settledCount / jobs.length) * 100);
      if (fill) fill.style.width = p + '%';
      if (pct)  pct.textContent  = p + '%';
    }
    loadJobs(jobs, function () { settledCount++; progress(); }, function () {
      clearInterval(sloganTimer);
      done();
    });
  }

  // ── Female set — background load after start ───────────────────────────────
  // Everything that needs it (gender filter, live scene) goes through
  // whenGenderReady(), which waits for the set if it's still on its way.
  var femaleSet = { state: 'idle', waiters: [] }; // idle → loading → ready
  function loadFemaleSet() {
    if (femaleSet.state !== 'idle') return;
    femaleSet.state = 'loading';
    loadJobs(preloadJobs(true), null, function () {
      tagFemaleSet();
      femaleSet.state = 'ready';
      var waiters = femaleSet.waiters;
      femaleSet.waiters = [];
      waiters.forEach(function (cb) { try { cb(); } catch (e) { console.error(e); } });
    });
  }
  function whenGenderReady(gender, cb) {
    if (gender !== 'female' || femaleSet.state === 'ready') { cb(); return; }
    femaleSet.waiters.push(cb);
    loadFemaleSet();
  }

  // Strip everything but ASCII letters and lowercase. Used to compare a
  // displayed part name like "Mud Guard" against any raw mesh prefix Blender
  // might have shipped — "mud_guard", "mudguard", "Mud-Guard", "Mud.001 Guard".
  function anchorNorm(s) { return (s || '').replace(/[^a-z]/gi, '').toLowerCase(); }
  function partOverrideKey(s) {
    return P.normalizePartKey
      ? P.normalizePartKey(s)
      : (s || '').replace(/@\S*/g, '').replace(/_\d+$/, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }
  function partKindOverridePrefixes() {
    var seen = {};
    Object.keys(D.partKindOverrides || {}).forEach(function (shoeId) {
      Object.keys(D.partKindOverrides[shoeId] || {}).forEach(function (prefix) {
        seen[prefix] = true;
      });
    });
    return Object.keys(seen);
  }
  function normalizedPartKindOverrides(shoeId) {
    var ov = D.partKindOverrides && D.partKindOverrides[shoeId];
    var out = {};
    Object.keys(ov || {}).forEach(function (prefix) {
      out[partOverrideKey(prefix)] = ov[prefix];
    });
    return out;
  }
  function explicitPartOverrideKind(shoeId, prefix) {
    return normalizedPartKindOverrides(shoeId)[partOverrideKey(prefix)] || null;
  }

  // Ground-truth detection. The anchor shoe (Club by convention) has named
  // meshes whose physical meaning we know — "Toe Cap" is leather, "Mud Guard"
  // is suede. We find each named part's outside mesh (highest _N suffix —
  // the visible top layer), look up its material's diffuse-image bytes in
  // the GLB JSON, and MD5 those bytes. That MD5 becomes the canonical
  // signature for that kind. Every other shoe in the catalog gets the same
  // tagging just by hashing each material's diffuse image — any match wins.
  //
  // The list of named parts lives in D.anchorParts. Add to it if more
  // reference materials emerge; remove an entry to opt out.
  //
  // For shoes whose textures don't match (Desert/City might ship unique
  // leather files), the chip swap still works because preload.referenceTextures
  // holds Club's actual THREE.Texture instances + material props.
  function detectMaterialKinds() {
    var anchorId = preload.models.club ? 'club' : null;
    if (!anchorId) {
      for (var i = 0; i < D.shoes.length; i++) {
        if (preload.models[D.shoes[i].id]) { anchorId = D.shoes[i].id; break; }
      }
    }
    var anchor    = anchorId && preload.models[anchorId];
    var anchorBuf = anchorId && preload.modelBuffers[anchorId];
    if (!anchor || !anchorBuf) {
      console.warn('[vitrine] no anchor model available — material detection skipped');
      return;
    }

    // GLBs baked by vitrine-core (0.3.3+) carry their kinds in material extras
    // — no image hashing at all. The anchor's hashes are only needed to
    // detect an unbaked GLB.
    function baked(gltf) { return !!(P.isKindBaked && P.isKindBaked(gltf)); }
    var canonicalMd5ToKind = {};
    if (!baked(anchor)) {
      var parsedAnchor = P.parseGlbImages(anchorBuf);
      (D.anchorParts || []).forEach(function (entry) {
        var md5 = anchorPartImageMd5(anchor, parsedAnchor, entry.name);
        if (md5) canonicalMd5ToKind[md5] = entry.kind;
      });
    }

    // Pinned overrides from data win — keeps the catalog's known images mapped
    // even if an anchor mesh name changes in a future GLB export.
    Object.keys(D.materialMd5ToKind || {}).forEach(function (h) {
      canonicalMd5ToKind[h] = D.materialMd5ToKind[h];
    });

    var primaryKind = D.primaryMaterialKind || D.materialKinds[D.materialKinds.length - 1].id;

    var anchorRefsCaptured = false;
    function tagShoeSet(models, buffers, md5Store) {
      var ids = Object.keys(models || {});
      if (anchorId && models[anchorId]) {
        ids = ids.filter(function (id) { return id !== anchorId; });
        ids.unshift(anchorId);
      }
      ids.forEach(function (shoeId) {
        var shoe = D.shoes.find(function (s) { return s.id === shoeId; });
        if (!shoe || !shoe.glb) return;
        var gltf = models[shoeId];
        var buf  = buffers[shoeId];
        if (!gltf || !buf) return;
        var shoeMd5ToKind;
        if (baked(gltf)) {
          shoeMd5ToKind = P.bakedMd5ToKind(gltf); // kinds already on the materials
        } else {
          shoeMd5ToKind = buildShoeMd5ToKind(gltf, buf, canonicalMd5ToKind, primaryKind, shoeId);
          P.tagMaterialKindsByMaterialIndex(gltf, buf, shoeMd5ToKind);
          P.tagMaterialKindsFromGltf(gltf, buf, shoeMd5ToKind);
        }
        md5Store[shoeId] = shoeMd5ToKind;
        if (shoeId === anchorId && !anchorRefsCaptured) {
          captureReferencesFromAnchor(anchor, anchorBuf, preload.shoeMd5ToKind[anchorId] || canonicalMd5ToKind);
          anchorRefsCaptured = true;
        }
        P.normalizeLaces(gltf.scene);
      });
    }

    // Tag all shoes first.
    tagShoeSet(preload.models, preload.modelBuffers, preload.shoeMd5ToKind);
    // Ensure references are captured before any baking.
    if (!anchorRefsCaptured) {
      captureReferencesFromAnchor(anchor, anchorBuf, preload.shoeMd5ToKind[anchorId] || canonicalMd5ToKind);
      anchorRefsCaptured = true;
    }
    // Bake overrides after references are captured.
    Object.keys(preload.models || {}).forEach(function (shoeId) {
      var gltf = preload.models[shoeId];
      if (gltf) P.bakePartKindOverrides(gltf.scene, D.partKindOverrides && D.partKindOverrides[shoeId], preload.referenceTextures, preload.referenceMaterials);
    });

    // The female set arrives later (loadFemaleSet) and is tagged by
    // tagFemaleSet() with the same canonical map and anchor references.
    detection = { tagShoeSet: tagShoeSet };
  }

  // Set by detectMaterialKinds (male set, at boot); used for the female set.
  var detection = null;
  function tagFemaleSet() {
    if (!detection) return;
    detection.tagShoeSet(preload.modelsFemale, preload.modelBuffersFemale, preload.shoeMd5ToKindFemale);
    // Bake female models.
    Object.keys(preload.modelsFemale || {}).forEach(function (shoeId) {
      var gltf = preload.modelsFemale[shoeId];
      if (gltf) P.bakePartKindOverrides(gltf.scene, D.partKindOverrides && D.partKindOverrides[shoeId], preload.referenceTextures, preload.referenceMaterials);
    });
  }

  // For one shoe, return its concrete md5→kind map.
  //   - Collect this shoe's distinct body-image MD5s.
  //   - Each MD5 that matches a canonical (anchor-derived) hash → that kind;
  //     today the only canonical kind is suede.
  //   - Every other body MD5 → primaryKind (leather).
  //
  // Body identification walks THREE.scene (where the real mesh names live)
  // and uses gltf.parser.associations to map each mesh back to its JSON
  // primitive / material. The JSON's meshes[i].name is empty in these GLBs
  // — the name lives on json.nodes[i] — so a JSON-only filter would reject
  // every body mesh.
  function buildShoeMd5ToKind(gltf, arrayBuffer, canonicalMd5ToKind, primaryKind, shoeId) {
    var parsed = P.parseGlbImages(arrayBuffer);
    if (!parsed.json) return {};
    var json = parsed.json;
    var assoc = gltf.parser && gltf.parser.associations;
    if (!assoc || typeof assoc.get !== 'function') return {};

    // Cache image MD5 by JSON material index — avoid recomputing if the same
    // material is shared by multiple body meshes.
    var md5ByMat = {};
    function md5OfMaterial(mIdx) {
      if (md5ByMat.hasOwnProperty(mIdx)) return md5ByMat[mIdx];
      md5ByMat[mIdx] = null;
      var mat = (json.materials || [])[mIdx];
      if (!mat || !mat.pbrMetallicRoughness || !mat.pbrMetallicRoughness.baseColorTexture) return null;
      var tex = (json.textures || [])[mat.pbrMetallicRoughness.baseColorTexture.index];
      if (!tex || typeof tex.source !== 'number') return null;
      var img = parsed.images[tex.source];
      if (!img || !img.bytes) return null;
      md5ByMat[mIdx] = P.md5(img.bytes);
      return md5ByMat[mIdx];
    }

    var seenMd5 = {};
    var distinctMd5s = [];
    gltf.scene.traverse(function (obj) {
      if (!obj.isMesh) return;
      var prefix = P.meshPrefix(obj.name);
      if (!prefix) return;
      if (P.isLaceName(obj.name)) return;
      if (P.isStitchName(prefix)) return;
      if (P.isSoleName(prefix)) return;
      if (P.isWeltName(prefix)) return;
      if (/^Part([.\s_]|$)/i.test(prefix) && !explicitPartOverrideKind(shoeId, prefix)) return;

      var a = assoc.get(obj);
      if (!a) return;
      var meshIdx = a.index !== undefined ? a.index : a.meshes;
      var primIdx = typeof a.primitives === 'number' ? a.primitives : 0;
      var jm = (json.meshes || [])[meshIdx];
      if (!jm) return;
      var prim = (jm.primitives || [])[primIdx];
      if (!prim || typeof prim.material !== 'number') return;
      var h = md5OfMaterial(prim.material);
      if (!h) return;
      if (!seenMd5[h]) { seenMd5[h] = true; distinctMd5s.push(h); }
    });
    // Grounded assignment — no sorting, no elimination, no guessing.
    //   match a canonical hash  → that kind (today: suede)
    //   everything else on body → primaryKind (leather)
    // The canonical suede image (Club's Mud Guard) is reused byte-for-byte by
    // every shoe that actually has suede, so an exact-byte MD5 finds it
    // everywhere; colour is a separate baseColorFactor tint in the GLB, not
    // baked into the image, so the hash is stable across colourways. Leather
    // ships as several authored variants — it's the default bucket, so its
    // hashes don't need to match anything. Shoes with all-leather body images
    // (City, Desert) therefore never get a phantom suede here: none of their
    // body images match the canonical suede hash. (City still ends up mixed —
    // its aglets are forced to suede via D.partKindOverrides, applied at bake
    // time, not by this image-hash detection.)
    var out = {};
    distinctMd5s.forEach(function (h) {
      out[h] = canonicalMd5ToKind[h] || primaryKind;
    });
    return out;
  }

  // Find a named part's outside mesh (highest _N suffix) in the anchor scene
  // and return the MD5 of its baseColorTexture image bytes. Match is very
  // loose: the partName from data is what the Vitrine *displays* (via
  // formatPartLabel). The raw mesh prefix could be "mud_guard", "mudguard",
  // "Mud-Guard", "Mud.001 Guard", etc. We strip everything that isn't a
  // letter and lowercase both sides — so any of those match.
  function anchorPartImageMd5(gltf, parsed, partName) {
    var target = anchorNorm(partName);
    if (!target) return null;
    var matches = [];
    var allBodyPrefixes = [];
    gltf.scene.traverse(function (obj) {
      if (!obj.isMesh) return;
      var rawPrefix = P.meshPrefix(obj.name);
      var prefix = anchorNorm(rawPrefix);
      if (prefix === target) matches.push(obj);
      if (rawPrefix && allBodyPrefixes.indexOf(rawPrefix) === -1) allBodyPrefixes.push(rawPrefix);
    });
    if (!matches.length) {
      console.warn('[vitrine] anchor part "' + partName + '" (normalised "' + target + '") not found. Available mesh prefixes:', allBodyPrefixes);
      return null;
    }
    var outside = P.outsideMesh(matches);
    if (!outside) return null;

    // Map the outside mesh to its JSON material index via parser.associations.
    var assoc = gltf.parser && gltf.parser.associations;
    if (!assoc || typeof assoc.get !== 'function') return null;
    var a = assoc.get(outside);
    if (!a) return null;
    var meshIdx = a.index !== undefined ? a.index : a.meshes;
    var primIdx = typeof a.primitives === 'number' ? a.primitives : 0;
    var jm = (parsed.json.meshes || [])[meshIdx];
    if (!jm) return null;
    var prim = (jm.primitives || [])[primIdx];
    if (!prim || typeof prim.material !== 'number') return null;
    var mat = (parsed.json.materials || [])[prim.material];
    if (!mat || !mat.pbrMetallicRoughness || !mat.pbrMetallicRoughness.baseColorTexture) return null;
    var tex = (parsed.json.textures || [])[mat.pbrMetallicRoughness.baseColorTexture.index];
    if (!tex || typeof tex.source !== 'number') return null;
    var img = parsed.images[tex.source];
    if (!img || !img.bytes) return null;
    return P.md5(img.bytes);
  }

  // Helper: walk anchor's scene, find materials with cfKind, capture their
  // THREE.Texture and the key material properties (roughness, metalness, maps,
  // texture transforms) into preload.referenceTextures + preload.referenceMaterials.
  function captureReferencesFromAnchor(gltf, arrayBuffer, md5ToKind) {
    if (!preload.referenceTextures) preload.referenceTextures = {};
    if (!preload.referenceMaterials) preload.referenceMaterials = {};
    gltf.scene.traverse(function (obj) {
      if (!obj.isMesh) return;
      var mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach(function (m) {
        if (!m || !m.map) return;
        var kind = m.userData && m.userData.cfKind;
        if (!kind || preload.referenceTextures[kind]) return;
        preload.referenceTextures[kind]  = m.map;
        preload.referenceMaterials[kind] = snapshotMaterialProps(m);
      });
    });
  }

  // Snapshot the shine/detail properties to carry across a Leather/Suede swap
  // (roughness, metalness, normal/bump/ao maps, env + emissive intensity) so the
  // swapped mesh looks like the source. The diffuse map + its UV transform come
  // straight from the source texture in applyMaterialKind, not from here.
  function snapshotMaterialProps(m) { return K.snapshotMaterialProps(m); }

  // True for body (upper) meshes — the ones a leather/suede swap applies to.
  // True for body (upper) meshes — the ones a leather/suede swap applies to.
  function isBodyMesh(obj) { return K.isBodyMesh(obj, state.selectedShoe.id); }

  // Snapshot the freshly-loaded model's native body materials (run after tag,
  // before any swap). Powers per-shoe swap sources + in-place "Mixed" restore.
  function captureLiveModel(scene) {
    liveModel = { kindSource: {}, snapshot: {} };
    if (!scene) return;

    // Per-mesh native snapshot for EVERY body mesh — Mixed restores each layer
    // (edge/detail/inside/outside) to its own original material.
    scene.traverse(function (obj) {
      if (!isBodyMesh(obj)) return;
      var mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      var m = mats[0];
      if (!m || !m.map) return;
      var kind = (m.userData && m.userData.cfKind) || null;
      liveModel.snapshot[obj.name] = { map: m.map, props: snapshotMaterialProps(m), kind: kind };
    });

    // Per-kind swap source (shared with the product page).
    liveModel.kindSource = K.kindSources(scene, state.selectedShoe.id);
  }

  // Force specific parts to a material that differs from what the GLB ships
  // (e.g. surplus's aglet is authored in leather but should read as suede).
  // Runs after captureLiveModel so kindSwapSource already holds the genuine
  // per-kind textures; updates the snapshot so Mixed keeps the override.
  function applyPartKindOverrides(scene, shoeId) {
    var ov = D.partKindOverrides && D.partKindOverrides[shoeId];
    if (!ov || !scene) return;
    var normalized = normalizedPartKindOverrides(shoeId);
    var groups = {};
    scene.traverse(function (obj) {
      // Allowlist-driven: act on ANY mesh whose prefix is explicitly listed in
      // the override map — even Part-prefixed / special meshes that isBodyMesh
      // and the groupers exclude — so a GLB-authored "wrong" part can be forced
      // to the right kind regardless of how it was named.
      if (!obj.isMesh || !obj.name) return;
      var prefix = P.meshPrefix(obj.name);
      var kind = prefix && normalized[partOverrideKey(prefix)];
      if (prefix && kind) (groups[prefix] = groups[prefix] || { kind: kind, meshes: [] }).meshes.push(obj);
    });
    Object.keys(groups).forEach(function (prefix) {
      var kind = groups[prefix].kind;
      var src  = kindSwapSource(kind);
      if (!src) return;
      P.applyMaterialKind(groups[prefix].meshes, kind, src.tex, 'override_' + prefix, src.props);
      groups[prefix].meshes.forEach(function (m) {
        var snap = m.name && liveModel.snapshot[m.name];
        if (snap) { snap.kind = kind; snap.map = src.tex; snap.props = src.props; }
      });
    });
  }

  // Bake the per-shoe part-kind overrides into a model's materials at PRELOAD,
  // straight onto the shared GLB (after MD5 tagging). Every consumer that clones
  // that GLB — the Design carousel and the offscreen thumbnails — then inherits
  // the corrected material AND its cfKind, so colourway slotting drops the part
  // into the right body bucket (e.g. surplus's Aiglet/In_Detail/Part36 read as
  // suede → body2) without anyone re-running the override. The single live model
  // loads its own fresh GLB and re-applies the same override via
  // applyPartKindOverrides; this is the shared-clone counterpart.
  //
  // Self-contained (no liveModel): the per-kind swap source is derived from this
  // model's own outside meshes, the same way captureLiveModel does it.
  function bakePartKindOverrides(scene, shoeId) {
    var ov = D.partKindOverrides && D.partKindOverrides[shoeId];
    if (!ov || !scene) return;
    P.bakePartKindOverrides(scene, ov, preload.referenceTextures, preload.referenceMaterials);
  }

  // Pick the diffuse texture + props to swap a part TO `kind`. Prefer the
  // shoe's own native texture for that kind (exact scale + look for this
  // model); fall back to Club's captured reference when the shoe doesn't ship
  // that kind (e.g. an all-leather shoe being forced to suede).
  function kindSwapSource(kind) {
    var native = liveModel.kindSource[kind];
    if (native && native.tex) return native;
    var refTex = preload.referenceTextures && preload.referenceTextures[kind];
    if (refTex) return { tex: refTex, props: preload.referenceMaterials[kind] };
    return null;
  }

  // Resolve a shoe or sole asset to its CDN URL. The map is emitted by the markup
  // snippet so Shopify's cache-buster query string survives.
  function isFemaleShape() { return state.shoeGender === 'female'; }
  function shoeSet(gender) { return gender === 'female' ? preload.modelsFemale : preload.models; }
  function bufferSet(gender) { return gender === 'female' ? preload.modelBuffersFemale : preload.modelBuffers; }
  function md5MapSet(gender) { return gender === 'female' ? preload.shoeMd5ToKindFemale : preload.shoeMd5ToKind; }
  function currentShoeModels() { return isFemaleShape() ? preload.modelsFemale : preload.models; }
  function currentSoleModels() { return isFemaleShape() ? preload.solesFemale : preload.soles; }
  function shoeUrl(id) {
    return (w.VitrineAssets && (isFemaleShape() ? w.VitrineAssets.shoesFemale : w.VitrineAssets.shoes) && (isFemaleShape() ? w.VitrineAssets.shoesFemale[id] : w.VitrineAssets.shoes[id])) || '';
  }
  function soleUrl(id) {
    return (w.VitrineAssets && (isFemaleShape() ? w.VitrineAssets.solesFemale : w.VitrineAssets.soles) && (isFemaleShape() ? w.VitrineAssets.solesFemale[id] : w.VitrineAssets.soles[id])) || '';
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PROGRESS
  // ──────────────────────────────────────────────────────────────────────────
  // Every step is part of the flow now (Customize included), so the progress
  // strip + dial list them all.
  var visibleSteps = STEPS;

  function renderProgress() {
    progressEl.innerHTML = visibleSteps.map(function (s) {
      return '<button class="vt-progress-step" data-step="' + s.slug + '">'
           + '<span class="vt-progress-num">' + String(s.index).padStart(2, '0') + '</span>'
           + '<span class="vt-progress-name">' + s.title + '</span>'
           + '</button>';
    }).join('');
    progressEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.vt-progress-step');
      if (!btn) return;
      var slug = btn.dataset.step;
      if (canJumpTo(slug)) jumpTo(slug);
    });
    setupProgressDial();
  }

  // Mobile progress ring + step popup. The ring shows the current step number with
  // an arc for how far along the build is; tapping opens a popup of step titles.
  // Steps the user can't jump to yet are greyed out. The popup is positioned in JS
  // (fixed, anchored under the dial) so it always lands on-screen.
  var RING_CIRCUM = 2 * Math.PI * 16; // r=16 in the SVG viewBox
  // Exposed by setupProgressDial so other UI (the review "Edit" button) can open
  // the same steps popup, anchored to whatever element triggered it.
  var openStepsPopup = function () {};
  function setupProgressDial() {
    var dial = document.getElementById('vt-progress-dial');
    var pop  = document.getElementById('vt-progress-pop');
    if (!dial || !pop) return;

    pop.innerHTML = visibleSteps.map(function (s) {
      return '<button class="vt-progress-pop-item" type="button" role="menuitem" data-step="' + s.slug + '">'
           + '<span class="vt-progress-pop-num">' + String(s.index).padStart(2, '0') + '</span>'
           + '<span class="vt-progress-pop-name">' + s.title + '</span>'
           + '</button>';
    }).join('');

    var anchorEl = dial; // element the popup is positioned under
    function closePop() {
      pop.classList.add('vt-hidden');
      dial.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocClick, true);
    }
    function onDocClick(e) {
      if (pop.contains(e.target) || (anchorEl && anchorEl.contains(e.target))) return;
      closePop();
    }
    function openPop(anchor) {
      anchorEl = anchor || dial;
      refreshProgressPop();
      pop.classList.remove('vt-hidden');
      dial.setAttribute('aria-expanded', 'true');
      // Anchor the popup's right edge under the trigger, clamped on-screen.
      var r = anchorEl.getBoundingClientRect();
      pop.style.top   = (r.bottom + 8) + 'px';
      pop.style.right = Math.max(8, window.innerWidth - r.right) + 'px';
      document.addEventListener('click', onDocClick, true);
    }
    openStepsPopup = openPop; // expose for the review "Edit" button

    dial.addEventListener('click', function () {
      if (pop.classList.contains('vt-hidden')) openPop(dial); else closePop();
    });
    pop.addEventListener('click', function (e) {
      var item = e.target.closest('.vt-progress-pop-item');
      if (!item || item.classList.contains('vt-progress-pop-item--disabled')) return;
      closePop();
      var slug = item.dataset.step;
      if (canJumpTo(slug)) jumpTo(slug);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.classList.contains('vt-hidden')) closePop();
    });
  }

  // Sync the popup items' active/disabled state to the current step.
  function refreshProgressPop() {
    var pop = document.getElementById('vt-progress-pop');
    if (!pop) return;
    Array.prototype.forEach.call(pop.children, function (item) {
      var slug = item.dataset.step;
      var active = slug === state.currentStep || (state.currentStep === 'parts' && slug === 'review');
      var jumpable = canJumpTo(slug);
      item.classList.toggle('vt-progress-pop-item--active', active);
      item.classList.toggle('vt-progress-pop-item--disabled', !jumpable);
      item.disabled = !jumpable;
    });
  }

  function updateProgress() {
    var stepIdx = visibleSteps.findIndex(function (s) { return s.slug === state.currentStep; });
    Array.prototype.forEach.call(progressEl.children, function (el, i) {
      el.classList.toggle('vt-progress-step--active',   i === stepIdx);
      el.classList.toggle('vt-progress-step--done',     i <  stepIdx);
      el.classList.toggle('vt-progress-step--reachable', i <= stepIdx + 1);
    });

    // Mobile dial: number inside, ring drawn as equal-sized arcs — one per step —
    // that fill in as the build progresses (rather than one continuous arc).
    if (stepIdx >= 0) {
      var numEl  = document.getElementById('vt-progress-dial-num');
      var ringFg = document.getElementById('vt-progress-ring-fg');
      var ringBg = document.querySelector('.vt-progress-ring-bg');
      if (numEl)  numEl.textContent = String(visibleSteps[stepIdx].index);
      var N = visibleSteps.length;
      var done = stepIdx + 1;                 // segments filled (incl. current)
      var gap  = 3;                           // gap between arcs, SVG units
      var seg  = RING_CIRCUM / N - gap;       // each arc's drawn length
      if (seg < 1) { seg = RING_CIRCUM / N * 0.6; gap = RING_CIRCUM / N * 0.4; }
      // Track: N faint equal arcs all the way round.
      if (ringBg) ringBg.style.strokeDasharray = seg.toFixed(2) + ' ' + gap.toFixed(2);
      // Fill: the first `done` arcs, then one long gap covering the rest (the
      // pattern sums to the full circumference so it doesn't repeat).
      if (ringFg) {
        var parts = [];
        for (var s = 0; s < done; s++) { parts.push(seg.toFixed(2)); parts.push(gap.toFixed(2)); }
        var rest = RING_CIRCUM - done * (seg + gap);
        if (rest < 0) rest = 0;
        parts[parts.length - 1] = (gap + rest).toFixed(2); // extend the trailing gap
        ringFg.style.strokeDasharray  = parts.join(' ');
        ringFg.style.strokeDashoffset = '0';
      }
      // Sync the compact header title (shown on mobile between brand and dial)
      var hdrTitleEl = document.getElementById('vt-header-step-title');
      if (hdrTitleEl) hdrTitleEl.textContent = visibleSteps[stepIdx].title;
    }
  }

  function canJumpTo(slug) {
    // From Customize or Review (the tail of the flow) you can jump anywhere;
    // otherwise only as far as the next step.
    if (state.currentStep === 'review' || state.currentStep === 'parts') return true;
    var fromIdx = stepIndex(state.currentStep);
    var toIdx   = stepIndex(slug);
    return toIdx <= fromIdx + 1;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // NAVIGATE
  // ──────────────────────────────────────────────────────────────────────────

  // Has the user touched anything? Once true, we warn before letting them leave.
  // Cleared only by a fresh page load — once it's set, every step they took
  // counts as progress they could lose.
  var EXIT_WARNING = 'Leaving the Vitrine? Your progress will be lost.';
  // The build is safely stored in the cart — freshly added this session, or
  // reopened from a cart line that's still there. Editing afterwards is handled by
  // the cart actions ("Edit shoe in cart" / "Add new shoe to cart"), so there's
  // nothing to lose by navigating away (e.g. "Take photos" → the scan page).
  function savedInCart() {
    return state.addedToCart || (state.editingLineKey && state.editLineInCart);
  }
  var leaveOk = false; // beta: the customer already confirmed leaving in its own dialog
  function hasProgress() {
    if (leaveOk) return false;
    if (boothActive) return false;   // kiosk demo — nothing of the visitor's to lose
    if (savedInCart()) return false; // saved → skip the "you'll lose progress" warning
    return state.currentStep !== 'design'
        || Object.keys(state.partColorsByMeshName).length > 0
        || Object.keys(state.partMaterialsByMeshName).length > 0
        || (state.notes && state.notes.length > 0);
  }

  // Browser-history nav: every step push adds a state entry; popstate replays
  // the step it stored. A 'guard' state sits behind the initial step so when
  // the user hits back past step 1, we can catch it and confirm before letting
  // the browser actually unload the page.
  function setupHistoryNav() {
    if (typeof history === 'undefined' || !history.pushState) return;
    history.replaceState({ vt: 'guard' }, '');
    history.pushState({ vt: 'step', step: 'design' }, '');

    window.addEventListener('popstate', function (e) {
      var st = e.state || {};
      if (st.vt === 'step') {
        // Back to the shoes with colours picked → ask first (askBackToShoes).
        // × puts the step the customer was on back on the history stack.
        if (st.step === 'design' && state.currentStep !== 'design' && colorsWorthKeeping()) {
          var from = state.currentStep;
          askBackToShoes(
            function () { goToStep('design', { instant: true, noPush: true }); },
            function () { history.pushState({ vt: 'step', step: from }, ''); }
          );
          return;
        }
        // Back/forward landed on a known step — navigate without pushing again.
        goToStep(st.step, { instant: true, noPush: true });
        return;
      }
      if (st.vt === 'guard') {
        // User pressed back past the entry step. Confirm before leaving.
        if (!hasProgress() || confirm(EXIT_WARNING)) {
          history.back(); // step past the guard to actually leave the page
        } else {
          // Cancelled — re-push the current step so back stays caught.
          history.pushState({ vt: 'step', step: state.currentStep }, '');
        }
      }
    });
  }

  // The brand mark in the header is a link to the storefront homepage. If the
  // user has progress, intercept the click and confirm before navigating away.
  function setupBrandLink() {
    var link = document.getElementById('vt-brand-link');
    if (!link) return;
    link.addEventListener('click', function (e) {
      if (hasProgress() && !confirm(EXIT_WARNING)) e.preventDefault();
    });
  }

  function goToStep(slug, opts) {
    opts = opts || {};
    var toIdx = -1;
    var stepPanels = Array.prototype.slice.call(stageEl.querySelectorAll('.vt-step'));
    stepPanels.forEach(function (p, i) { if (p.dataset.step === slug) toIdx = i; });
    if (toIdx === -1) return;

    // Stop any running pulse + restore an in-progress flash NOW, before the
    // destination step repaints — otherwise the flash's white emissive gets
    // baked into the freshly-cloned materials and the part stays tinted.
    stopStepPulse();

    // Leaving the Design carousel: if the centred shoe changed since we entered,
    // its meshes differ, so part overrides keyed by the old shoe no longer apply.
    if (state.currentStep === 'design' && slug !== 'design') commitDesignSelection();

    state.currentStep = slug;
    // Once Review has been seen, every earlier step's primary button turns into a
    // one-tap jump back to Review (see updateFooterLabels + nextStep).
    if (slug === 'review') state.reachedReview = true;

    // Push a history entry so the browser back button navigates back through
    // the Vitrine instead of leaving the page. `noPush` is set when this call
    // is itself driven by a popstate event — we don't want to re-push then.
    if (!opts.noPush && typeof history !== 'undefined' && history.pushState) {
      history.pushState({ vt: 'step', step: slug }, '');
    }

    stageEl.classList.toggle('vt-stage--instant', !!opts.instant);
    stepPanels.forEach(function (panel, i) {
      panel.classList.remove('vt-step--current', 'vt-step--past');
      if (i === toIdx)    panel.classList.add('vt-step--current');
      else if (i < toIdx) panel.classList.add('vt-step--past');
    });

    // Canvas frame fades in/out as we move into / out of the canvas-on steps.
    // Design gets its own full-bleed treatment via vt-stage--carousel.
    var canvasOn  = D.stepsWithCanvas.indexOf(slug) !== -1;
    var carouselOn = slug === 'design';
    stageEl.classList.toggle('vt-stage--canvas-on', canvasOn);
    stageEl.classList.toggle('vt-stage--carousel', carouselOn);
    stageEl.dataset.step = slug;
    if (viewer) viewer.setMode(carouselOn ? 'carousel' : 'single');

    if (opts.instant) requestAnimationFrame(function () { stageEl.classList.remove('vt-stage--instant'); });

    updateProgress();
    updateFooterLabels();
    onEnterStep(slug);
  }

  // Each step's "just became visible" hook.
  function onEnterStep(slug) {
    if (slug === 'design') {
      // Re-sync the carousel (filter/selection may have changed) and remember the
      // shoe we entered on so commitDesignSelection can detect a real change.
      designEntryShoeId = state.selectedShoe.id;
      if (carousel) recenterCarousel();
      // Back here with "Keep colors": every shoe wears the customer's colours
      // (or its nearest colourway) and the random rotation stays off.
      if (backChoice === 'keep') showKeptColorways();
      else startDesignColorwayRotation();
    } else {
      stopDesignColorwayRotation();
    }
    if (slug === 'colors') {
      renderColorwayGrid();       // re-order + re-mark for the chosen shoe
      renderColorwayThumbnails(); // refresh in case shoe changed
      syncColorwayLivePreview();
    }
    // First time we enter a canvas-on step, load the live scene. After that, only
    // reload if the user changed design or sole; lining and colour can be applied
    // to the existing scene without a reload.
    if (D.stepsWithCanvas.indexOf(slug) !== -1) ensureLiveScene();
    if (slug === 'review') { renderSummary(); refreshCartActions(); }
    if (slug === 'parts')  { onEnterPartsStep(); }
    if (beta) { if (slug === 'build') beta.onEnter(); else beta.onLeave(); }
    // Swing the camera to the area relevant to this step.
    if (viewer && D.stepsWithCanvas.indexOf(slug) !== -1) {
      viewer.focusView(slug === 'review' || slug === 'build' ? 'hero' : slug);
    }
    startStepPulse(slug); // slow-pulse the sole / lining on those steps (no-op elsewhere)
    refreshScrollFades(); // panel is now sized — recompute every rail's edge fade
  }

  // When the base shape changes in the carousel, part overrides keyed by the old
  // shoe's meshes are stale — clear them and force a fresh live-scene rebuild.
  function commitDesignSelection() {
    var changed = !!designEntryShoeId && state.selectedShoe.id !== designEntryShoeId;
    // The answer to "Keep colors as close as possible?" on the way back here.
    var keep = backChoice === 'keep', restart = backChoice === 'restart';
    backChoice = null;

    if (keep && changed && state.selectedColorway && state.selectedColorway.id) {
      // Same colourway if the new shoe offers it, else its nearest one.
      state.selectedColorway = closestColorway(state.selectedColorway, state.selectedShoe.id);
    } else if (BETA && (restart || !state.selectedColorway.id || changed)) {
      // Beta has no Colors step: the shoe arrives in the colourway it wore on the
      // carousel ("Shown in …") — on first entry, whenever the shape changes, and
      // on Restart.
      state.selectedColorway = designColorwayForShoe(state.selectedShoe.id);
    }

    if (keep && changed) {
      // Per-part overrides are keyed by mesh name but resolved per BUCKET
      // (VitrineKit.applyOverrides), so laces / stitching / sole / welt and any
      // part the two shoes share keep their colour and material. Only the
      // group-id maps are stale on another shoe.
      state.partColors = {};
      state.partMaterials = {};
      loadedShoeId = null;
    } else if (changed || restart) {
      state.partColors = {};
      state.partColorsByMeshName = {};
      state.partMaterials = {};
      state.partMaterialsByMeshName = {};
      state.materialMode = 'mixed';
      loadedShoeId = null; // ensureLiveScene rebuilds the model for the new shoe
    }
    designEntryShoeId = state.selectedShoe.id;
  }

  // ── Back to the shoes: keep the colours or restart? ─────────────────────────
  // Going back to the Design carousel with colours picked asks once:
  //   Keep colors as close as possible → the next shoe wears the same colourway
  //     (or its nearest) and every part override it can take;
  //   Restart → the next shoe starts fresh in its carousel colourway.
  // Applied when the customer leaves Design again (commitDesignSelection).
  var backChoice = null;   // 'keep' | 'restart' | null

  function colorsWorthKeeping() {
    if (state.currentStep === 'design') return false;
    if (Object.keys(state.partColorsByMeshName).length || Object.keys(state.partMaterialsByMeshName).length) return true;
    var dflt = designColorwayForShoe(state.selectedShoe.id);
    return !!(state.selectedColorway && state.selectedColorway.id && dflt && state.selectedColorway.id !== dflt.id);
  }

  function askBackToShoes(go, cancel) {
    var Card = window.VitrineCard;
    if (!colorsWorthKeeping() || !Card || !Card.choose) { go(); return; }
    Card.choose({
      eyebrow: 'Back to the shoes',
      title: 'Your colors',
      lead: 'Pick another shoe next. Should it wear the colors you chose?',
      choices: [
        { label: 'Keep colors as close as possible', primary: true, onPick: function () { backChoice = 'keep'; go(); } },
        { label: 'Restart', onPick: function () { backChoice = 'restart'; go(); } }
      ],
      onCancel: cancel
    });
  }

  // Any in-page route to another step (progress bar / dial).
  function jumpTo(slug) {
    if (slug === 'design' && state.currentStep !== 'design') askBackToShoes(function () { goToStep('design'); });
    else goToStep(slug);
  }

  // The new shoe's colourway nearest to cw: cw itself when offered, else the
  // smallest RGB distance over the two body colours.
  function closestColorway(cw, shoeId) {
    var pool = orderedColorwaysForShoe(shoeId) || [];
    if (!pool.length) return cw;
    for (var i = 0; i < pool.length; i++) if (pool[i].id === cw.id) return pool[i];
    function rgb(h) {
      h = String(h || '').replace('#', '');
      if (h.length === 3) h = h.replace(/./g, '$&$&');
      var n = parseInt(h, 16);
      return isNaN(n) ? [0, 0, 0] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function d(a, b) {
      var x = rgb(a), y = rgb(b);
      return Math.pow(x[0] - y[0], 2) + Math.pow(x[1] - y[1], 2) + Math.pow(x[2] - y[2], 2);
    }
    function col(c, k) { return c[k] && c[k].color; }
    var best = pool[0], bestD = Infinity;
    pool.forEach(function (c) {
      var dd = d(col(c, 'body1'), col(cw, 'body1')) + d(col(c, 'body2') || col(c, 'body1'), col(cw, 'body2') || col(cw, 'body1'));
      if (dd < bestD) { bestD = dd; best = c; }
    });
    return best;
  }

  function nextStep() {
    if (BETA && state.currentStep === 'parts') { goToStep('build'); return; } // Customize is a sub-page of the build panel
    // Once Review has been seen, the primary button on any earlier step jumps
    // straight back to Review rather than stepping forward one slide at a time.
    if (state.reachedReview && state.currentStep !== 'review') { goToStep('review'); return; }
    var idx = stepIndex(state.currentStep);
    if (idx > -1 && idx < STEPS.length - 1) goToStep(STEPS[idx + 1].slug);
  }
  function prevStep() {
    if (BETA && state.currentStep === 'parts') { goToStep('build'); return; }
    var idx = stepIndex(state.currentStep);
    if (idx > 0) jumpTo(STEPS[idx - 1].slug);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP DESIGN — shoe grid with category filters
  // ──────────────────────────────────────────────────────────────────────────
  // The Design step is a live 3D coverflow carousel rendered into the global
  // canvas (vt-stage--carousel makes the canvas full-bleed). This panel only
  // holds the chrome that floats over it: filter chips, arrows, caption + dots.
  function renderDesignStep() {
    var panel = stepPanel('design');
    var genders = [
      { id: 'male',   label: 'Male Foot Shape'   },
      { id: 'female', label: 'Female Foot Shape' }
    ];
    // First-step note: reflect whether the signed-in customer already has measurements.
    var hasScan = !!(w.cfHasScan && w.cfHasScan());
    var scanNote = hasScan
      ? 'Your measurements are already on file — every pair is shaped to the exact contours of your own feet.'
      : 'Two effortless photographs, taken at home, capture your feet — and every pair is then shaped to their exact contours.';

    panel.innerHTML = stepHead('design')
      + '<div class="vt-design-carousel">'
      +   '<div class="vt-filters vt-carousel-filters" id="vt-shoe-filters">'
      +     genders.map(function (g) {
              return '<button class="vt-filter ' + (state.shoeGender === g.id ? 'vt-filter--active' : '') + '" data-gender="' + g.id + '">' + g.label + '</button>';
            }).join('')
      +     '<span class="vt-shape-note"><span class="vt-shape-note-l1">For visualization only.</span><span class="vt-shape-note-l2">' + scanNote + '</span></span>'
      +   '</div>'
      +   '<button class="vt-carousel-arrow vt-carousel-arrow--prev" data-car="prev" aria-label="Previous shoe">‹</button>'
      +   '<button class="vt-carousel-arrow vt-carousel-arrow--next" data-car="next" aria-label="Next shoe">›</button>'
      +   '<div class="vt-carousel-caption">'
      +     '<span class="vt-carousel-name" id="vt-carousel-name"></span>'
      +     '<span class="vt-carousel-blurb" id="vt-carousel-blurb"></span>'
      +     '<span class="vt-carousel-colorway" id="vt-carousel-colorway"></span>'
      +     '<div class="vt-carousel-dots" id="vt-carousel-dots"></div>'
      +   '</div>'
      + '</div>'
      + stepFoot('design', { hideBack: true });

    panel.addEventListener('click', function (e) {
      var filter = e.target.closest('.vt-filter');
      if (filter) {
        var newGender = filter.dataset.gender;
        if (newGender && newGender !== state.shoeGender) {
          state.shoeGender = newGender;
          panel.querySelectorAll('.vt-filter').forEach(function (b) {
            b.classList.toggle('vt-filter--active', b.dataset.gender === newGender);
          });
          // The female set may still be downloading — swap the carousel once
          // it's here (and only if this is still the chosen shape).
          rootEl.classList.add('vt-gender-loading');
          whenGenderReady(newGender, function () {
            if (state.shoeGender !== newGender) return;
            rootEl.classList.remove('vt-gender-loading');
            if (carousel) {
              carousel.setModels(currentShoeModels());
              carousel.setSoles(currentSoleModels());
            }
            rebuildCarouselShoes();
          });
        }
        return;
      }
      var arrow = e.target.closest('[data-car]');
      if (arrow && carousel) { arrow.dataset.car === 'next' ? carousel.next() : carousel.prev(); return; }
      var dot = e.target.closest('.vt-carousel-dot');
      if (dot && carousel) { carousel.goTo(parseInt(dot.dataset.idx, 10)); }
    });
  }

  // FULL rebuild of the carousel slots — clones every shoe model and repaints it.
  // Only do this when the model SET actually changes (boot + gender switch).
  // Rebuilding on every visit to Design re-cloned every model and leaked GPU
  // memory across back-and-forths, which on mobile lost the WebGL context: shoes
  // render black, the render loop then throws every frame ("error keeps
  // repeating"), and the tab can reload. Re-entry now uses recenterCarousel().
  function rebuildCarouselShoes() {
    if (!carousel) return;
    currentDesignShoes = D.shoes.slice();
    carousel.setShoes(currentDesignShoes);
    renderCarouselDots();
    recenterCarousel();
  }

  // Lightweight re-entry: roll the already-built carousel to the selected shoe
  // and refresh the caption. No slot rebuild, no fresh clones.
  function recenterCarousel() {
    if (!carousel) return;
    if (!currentDesignShoes.length) currentDesignShoes = D.shoes.slice();
    var idx = currentDesignShoes.indexOf(state.selectedShoe);
    if (idx > 0) carousel.goTo(idx);
    updateCarouselCaption(state.selectedShoe);
  }

  function renderCarouselDots() {
    var dots = document.getElementById('vt-carousel-dots');
    if (!dots) return;
    dots.innerHTML = currentDesignShoes.map(function (s, i) {
      return '<button class="vt-carousel-dot" data-idx="' + i + '" aria-label="' + s.name + '"></button>';
    }).join('');
  }

  // Carousel told us a new shoe rolled to the front. Ghosts (coming soon) never
  // become the selection — we just show "Coming soon" and disable Continue.
  function onCarouselChange(shoe, idx) {
    if (shoe && !shoe.comingSoon) state.selectedShoe = shoe;
    updateCarouselCaption(shoe);
    setCarouselBg(shoe);                 // background follows the centred shoe's colour
    var dots = document.getElementById('vt-carousel-dots');
    if (dots) dots.querySelectorAll('.vt-carousel-dot').forEach(function (d, i) {
      d.classList.toggle('vt-carousel-dot--active', i === idx);
    });
  }

  function updateCarouselCaption(shoe) {
    var nameEl  = document.getElementById('vt-carousel-name');
    var blurbEl = document.getElementById('vt-carousel-blurb');
    var cwEl    = document.getElementById('vt-carousel-colorway');
    var soon    = shoe && shoe.comingSoon;
    var cw      = shoe ? designColorwayForShoe(shoe.id) : null;
    if (nameEl)  nameEl.textContent  = shoe ? shoe.name : '';
    if (blurbEl) blurbEl.textContent = soon ? 'Coming soon' : (shoe ? shoe.blurb : '');
    if (cwEl)    cwEl.textContent    = (cw && !soon) ? 'Shown in ' + cw.label : '';
    var next = document.querySelector('.vt-step[data-step="design"] .vt-btn--next');
    if (next) {
      next.disabled = !!soon;
      next.classList.toggle('vt-btn--disabled', !!soon);
    }
  }

  // Create the carousel once the GLBs are preloaded and the viewer exists. Shares
  // the viewer's renderer + canvas, so there's only ever one WebGL context.
  function initCarousel() {
    if (carousel || !viewer || !w.VitrineCarousel) return;
    var canvas = document.getElementById('vt-canvas');
    assignDesignColorways();
    if (!designColorway) designColorway = randomDesignColorway();
    carousel = w.VitrineCarousel.create({
      models:   currentShoeModels(),
      soles:    currentSoleModels(),
      painter:  P,
      colorway: designColorway,
      canvas:   canvas
    });
    viewer.setCarousel(carousel);
    carousel.onChange(onCarouselChange);
    // Each shoe wears its own colourway on the carousel.
    if (carousel.setColorwayResolver) {
      carousel.setColorwayResolver(function (shoeId) { return designColorwayForShoe(shoeId); });
    }
    rebuildCarouselShoes();
    if (state.currentStep === 'design') viewer.setMode('carousel');
  }

  // The design slide only ever shows TWO-TONE colorways — body1 and body2 must be
  // visibly different colours — so the rotating hero never lands on a flat solid.
  function isTwoTone(cw) {
    return cw && cw.body1 && cw.body2 && cw.body1.color !== cw.body2.color;
  }
  function randomDesignColorway() {
    var pool = D.colorways.filter(isTwoTone);
    if (!pool.length) pool = D.colorways.slice();
    return pool[Math.floor(Math.random() * pool.length)] || D.initialColorway;
  }

  // A random colourway from a shoe's curated lead set (the colours called out for
  // that shoe), so the carousel mainly wears those palettes. Falls back to the
  // full ordered list, then any two-tone, if a shoe has no curated set.
  // Colourways every model can rotate through on the carousel, on top of its
  // curated lead set.
  var EXTRA_ROTATION_COLORWAYS = [];
  function rotationPoolForShoe(shoeId) {
    var ids = (K.SHOE_COLORWAY_ORDER[shoeId] && K.SHOE_COLORWAY_ORDER[shoeId].slice()) || [];
    EXTRA_ROTATION_COLORWAYS.forEach(function (id) { if (ids.indexOf(id) === -1) ids.push(id); });
    var byId = {};
    D.colorways.forEach(function (c) { byId[c.id] = c; });
    var pool = ids.map(function (id) { return byId[id]; }).filter(Boolean);
    if (!pool.length) pool = orderedColorwaysForShoe(shoeId);
    if (!pool.length) pool = D.colorways.slice();
    return pool;
  }
  function designColorwayForShoe(shoeId) {
    return (shoeId && designColorways[shoeId]) || designColorway || D.initialColorway;
  }
  // Give every real shoe a fresh random colourway, and make sure no two shoes wear
  // the same one this round. Shoes with the smallest pool pick first so distinct
  // assignment stays possible.
  function assignDesignColorways() {
    var used = {};
    D.shoes
      .filter(function (s) { return !!s.glb; })
      .map(function (s) { return { id: s.id, pool: rotationPoolForShoe(s.id) }; })
      .sort(function (a, b) { return a.pool.length - b.pool.length; })
      .forEach(function (entry) {
        var avail = entry.pool.filter(function (cw) { return !used[cw.id]; });
        var src   = avail.length ? avail : entry.pool;   // fall back if everything's taken
        var choice = src[Math.floor(Math.random() * src.length)];
        if (choice) { designColorways[entry.id] = choice; used[choice.id] = true; }
      });
    designColorway = designColorwayForShoe(state.selectedShoe && state.selectedShoe.id);
  }

  function setCarouselBg(shoe) {
    var cw = shoe ? designColorwayForShoe(shoe.id) : designColorway;
    if (!cw) return;
    // The visible slide-01 background is the carousel's 3D backdrop (the WebGL
    // canvas covers the CSS frame), so tint THAT to the centred shoe's colourway —
    // the same generative backdrop the Colors step uses, so it reads consistently.
    if (carousel && carousel.setBackdropColorway) carousel.setBackdropColorway(cw);
    if (stageEl) {
      stageEl.style.setProperty('--vt-carousel-bg', softMix(cw.body1.color, cw.body2.color));
      stageEl.style.setProperty('--vt-carousel-accent', cw.sole.color);
    }
  }

  // Re-paint the carousel with the current per-shoe colourways. The carousel
  // cross-fades each shoe's colours (opts.tween) so the change reads as the shoes
  // shifting colour rather than the whole slide flashing.
  function applyDesignColorways(opts) {
    opts = opts || {};
    if (!carousel || !carousel.setColorwayResolver) return;
    carousel.setColorwayResolver(function (shoeId) { return designColorwayForShoe(shoeId); }, { tween: !opts.instant });
    var center = carousel.getCenterShoe ? carousel.getCenterShoe() : state.selectedShoe;
    updateCarouselCaption(center);
    setCarouselBg(center);
  }

  function startDesignColorwayRotation() {
    if (!Object.keys(designColorways).length) assignDesignColorways();
    applyDesignColorways({ instant: true });
    if (designColorwayTimer) return;
    designColorwayTimer = setInterval(function () {
      if (state.currentStep !== 'design') return;
      assignDesignColorways();
      applyDesignColorways();           // cross-fade to the fresh palettes
    }, 5000);
  }

  // The carousel in the customer's colours: each shoe gets the chosen colourway
  // if it offers it, else its nearest (closestColorway). No rotation timer.
  function showKeptColorways() {
    stopDesignColorwayRotation();
    var cw = (state.selectedColorway && state.selectedColorway.id)
      ? state.selectedColorway
      : designColorwayForShoe(state.selectedShoe && state.selectedShoe.id);
    if (!cw) { startDesignColorwayRotation(); return; }
    D.shoes.forEach(function (s) {
      if (s.glb) designColorways[s.id] = closestColorway(cw, s.id);
    });
    designColorway = designColorwayForShoe(state.selectedShoe && state.selectedShoe.id);
    applyDesignColorways({ instant: true });
  }

  function stopDesignColorwayRotation() {
    if (!designColorwayTimer) return;
    clearInterval(designColorwayTimer);
    designColorwayTimer = null;
  }

  function softMix(a, b) {
    function hexToRgb(hex) {
      var h = (hex || '#ECEAE5').replace('#', '');
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16)
      };
    }
    var x = hexToRgb(a), y = hexToRgb(b), bone = hexToRgb('#ECEAE5');
    var m = { r: (x.r + y.r) / 2, g: (x.g + y.g) / 2, b: (x.b + y.b) / 2 };
    var out = {
      r: Math.round(m.r * 0.20 + bone.r * 0.80),
      g: Math.round(m.g * 0.20 + bone.g * 0.80),
      b: Math.round(m.b * 0.20 + bone.b * 0.80)
    };
    function two(n) { var s = n.toString(16); return s.length === 1 ? '0' + s : s; }
    return '#' + two(out.r) + two(out.g) + two(out.b);
  }

  function applyColorwayThumbnails() {
    Object.keys(thumbCache.cw).forEach(function (id) {
      var sel = '[data-thumb-for-cw="' + id + '"]';
      document.querySelectorAll(sel).forEach(function (img) { img.src = thumbCache.cw[id]; });
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP COLORS — large colourway boxes, ordered per shoe (no category chips)
  // ──────────────────────────────────────────────────────────────────────────
  // Lead colourways per shoe; anything not listed follows in the data's order.
  function orderedColorwaysForShoe(shoeId) { return K.orderedColorways(shoeId); }

  function renderColorsStep() {
    var panel = stepPanel('colors');
    panel.innerHTML = stepHead('colors')
      + '<div class="vt-step-body">'
      + '<p class="vt-step-lead">Choose a colourway to start from — you’ll refine every part’s colour and material afterwards.</p>'
      + '<div class="vt-rail">'
      +   '<button class="vt-carousel-arrow vt-carousel-arrow--prev" type="button" data-rail="prev" aria-label="Previous colourways">‹</button>'
      +   '<div class="vt-grid vt-grid--colorways vt-scroll-x" id="vt-cw-grid"></div>'
      +   '<button class="vt-carousel-arrow vt-carousel-arrow--next" type="button" data-rail="next" aria-label="More colourways">›</button>'
      + '</div>'
      + '</div>'
      + stepFoot('colors');

    renderColorwayGrid();
    bindRailArrows(panel.querySelector('.vt-rail'));
  }

  // Let a vertical wheel / trackpad gesture scroll a horizontal-only panel along
  // its one axis — so "scroll in any direction" moves the colorway rail sideways.
  // Also wires the edge-fade, so every rail bound here gets the scroll hint.
  function bindWheelHorizontal(el) {
    if (!el) return;
    setupScrollFade(el);
    if (el._wheelHBound) return;
    el._wheelHBound = true;
    el.addEventListener('wheel', function (e) {
      if (el.scrollWidth <= el.clientWidth) return;       // nothing to scroll → let the page handle it
      // Map whichever axis the user moved most onto the rail's one axis, so a
      // vertical OR horizontal wheel/trackpad gesture both scroll sideways.
      var delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (!delta) return;
      el.scrollLeft += delta;
      e.preventDefault();
    }, { passive: false });
  }

  // The slide-1 carousel arrows (‹ ›) reused to page a horizontal rail one card at
  // a time — a reliable affordance over the wheel/swipe gesture. Wraps the same
  // .vt-scroll-x scroller (wheel + edge-fade come from bindWheelHorizontal); the
  // arrows' shown/hidden state is driven by setupScrollFade via syncRailArrows.
  function bindRailArrows(rail) {
    if (!rail) return;
    var grid = rail.querySelector('.vt-scroll-x');
    if (!grid) return;
    bindWheelHorizontal(grid);
    if (rail._railArrowsBound) return;
    rail._railArrowsBound = true;
    function pageBy(dir) {
      var card = grid.firstElementChild;
      var gap  = parseFloat(getComputedStyle(grid).columnGap) || 16;
      var step = card ? card.getBoundingClientRect().width + gap : grid.clientWidth * 0.8;
      grid.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
    rail.querySelectorAll('[data-rail]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        pageBy(btn.dataset.rail === 'next' ? 1 : -1);
      });
    });
  }

  // If a .vt-scroll-x is wrapped in an arrow rail, hide whichever arrow points at
  // a wall — reuses the data-scroll state setupScrollFade already computed.
  function syncRailArrows(el) {
    var rail = el.closest && el.closest('.vt-rail');
    if (!rail) return;
    var s = el.dataset.scroll;
    var prev = rail.querySelector('[data-rail="prev"]');
    var next = rail.querySelector('[data-rail="next"]');
    if (prev) prev.classList.toggle('vt-carousel-arrow--off', s === 'none' || s === 'start');
    if (next) next.classList.toggle('vt-carousel-arrow--off', s === 'none' || s === 'end');
  }

  // DRY edge-fade for any horizontal scroller (.vt-scroll-x). Tags the element
  // with data-scroll = none|start|mid|end; the CSS mask then fades whichever side
  // still has content scrolled off, so the rail always signals "there's more".
  function setupScrollFade(el) {
    if (!el) return;
    function update() {
      var max = el.scrollWidth - el.clientWidth;
      el.dataset.scroll = max <= 1 ? 'none'
        : (el.scrollLeft <= 1 ? 'start' : (el.scrollLeft >= max - 1 ? 'end' : 'mid'));
      syncRailArrows(el);
    }
    if (!el._scrollFadeBound) {
      el._scrollFadeBound = true;
      el.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    }
    update();
  }
  // Recompute every rail's fade — call after a step is shown or content changes.
  function refreshScrollFades() {
    document.querySelectorAll('.vt-scroll-x').forEach(setupScrollFade);
  }

  function renderColorwayGrid() {
    var grid = document.getElementById('vt-cw-grid');
    if (!grid) return;
    grid.innerHTML = orderedColorwaysForShoe(state.selectedShoe.id).map(function (cw) {
      var active = cw.id === state.selectedColorway.id ? ' vt-card--active' : '';
      return '<div class="vt-card vt-card--cw' + active + '" role="button" tabindex="0" data-cw-id="' + cw.id + '" data-cw-cat="' + cw.cat + '">'
           + '<div class="vt-card-art">'
           +   '<img class="vt-card-img" src="' + PLACEHOLDER_IMG + '" data-thumb-for-cw="' + cw.id + '" alt="' + cw.label + '">'
           + '</div>'
           + '<div class="vt-card-meta">'
           +   '<div class="vt-cw-dots">' + cwDotsHtml(cw) + '</div>'
           +   '<span class="vt-card-name">' + cw.label + '</span>'
           + '</div>'
           + '</div>';
    }).join('');

    grid.querySelectorAll('.vt-card--cw').forEach(function (card) {
      card.addEventListener('click', function () {
        var cw = D.colorways.find(function (c) { return c.id === card.dataset.cwId; });
        if (!cw) return;
        // If the customer has per-part colour or material edits, warn them —
        // picking a colorway is a fresh canvas and silently wiping their work
        // is a bad surprise. Skip the prompt when there's nothing to lose.
        var hasColourEdits   = Object.keys(state.partColorsByMeshName).length > 0;
        var hasMaterialEdits = Object.keys(state.partMaterialsByMeshName).length > 0;
        if ((hasColourEdits || hasMaterialEdits)
            && !confirm('Picking a new colorway will reset your part colours' +
                        (hasMaterialEdits ? ' and material changes' : '') + '. Continue?')) {
          return;
        }
        state.selectedColorway = cw;
        // Reset overrides — a new colorway is a fresh canvas.
        state.partColors = {};
        state.partColorsByMeshName = {};
        state.partMaterials = {};
        state.partMaterialsByMeshName = {};
        state.materialMode = 'mixed';
        // Material swaps mutate live materials; only reloading the GLB fully
        // restores the original kinds. Same trick as resetPartOverrides.
        if (hasMaterialEdits) {
          loadedShoeId = null;
          if (typeof ensureLiveScene === 'function') ensureLiveScene();
        }
        syncColorwayLivePreview();
        grid.querySelectorAll('.vt-card--cw').forEach(function (c) { c.classList.remove('vt-card--active'); });
        card.classList.add('vt-card--active');
      });
    });
  }

  function syncColorwayLivePreview() {
    if (D.stepsWithCanvas.indexOf(state.currentStep) === -1) return;
    if (!viewer || !viewer.getModelScene()) return;
    repaintReview();
  }

  function cwDotsHtml(cw) {
    return [cw.body1, cw.body2, cw.sole, cw.laces, cw.stitch].map(function (s) {
      var border = s.color === '#FFFFFF' ? 'rgba(0,0,0,0.18)' : 'transparent';
      return '<span class="vt-cw-dot" style="background:' + s.color + ';border-color:' + border + '"></span>';
    }).join('');
  }

  function filterColorwayCards() {
    document.querySelectorAll('#vt-cw-grid .vt-card--cw').forEach(function (card) {
      var matches = state.colorwayFilter === 'all' || card.dataset.cwCat === state.colorwayFilter;
      card.classList.toggle('vt-card--filtered', !matches);
    });
    setupScrollFade(document.getElementById('vt-cw-grid')); // row width changed
  }

  // Render a styled thumbnail per colorway using the currently-selected shoe.
  // Runs one render per animation frame to avoid blocking. A new call cancels
  // the previous job so we never have two chains racing. Thumbnails are cached
  // by colorway id — switching shoes invalidates the cache.
  var thumbJob = null;
  var thumbJobToken = 0;
  var thumbJobShoeKey = null;
  function renderColorwayThumbnails() {
    if (!viewer) return;
    if (isFemaleShape() && femaleSet.state !== 'ready') {
      whenGenderReady('female', renderColorwayThumbnails);
      return;
    }
    var gltf = currentShoeModels()[state.selectedShoe.id];
    if (!gltf) return;
    var shoeKey = state.shoeGender + ':' + state.selectedShoe.id;
    // Drop cached colorway thumbs if the shoe or gender changed.
    if (thumbJobShoeKey !== shoeKey) {
      thumbCache.cw = {};
      thumbJobShoeKey = shoeKey;
    } else {
      // Same shoe — already cached, just reapply to current DOM and bail.
      applyColorwayThumbnails();
      if (Object.keys(thumbCache.cw).length >= D.colorways.length) return;
    }
    var token = ++thumbJobToken;
    thumbJob = { i: 0, gltf: gltf, sole: currentSoleModels().ground, token: token, shoeId: state.selectedShoe.id };
    requestAnimationFrame(stepColorwayThumb);
  }
  function stepColorwayThumb() {
    if (!thumbJob || thumbJob.token !== thumbJobToken) return;
    if (thumbJob.i >= D.colorways.length) { thumbJob = null; return; }
    var cw = D.colorways[thumbJob.i++];
    var shoeId = thumbJob.shoeId;
    var url = viewer.captureThumbnail(thumbJob.gltf, thumbJob.sole, shoeColorway(cw, shoeId), function (groups, colorway) {
      paintColorwayForShoe(groups, colorway, shoeId);
    });
    if (url) {
      thumbCache.cw[cw.id] = url;
      var sel = '[data-thumb-for-cw="' + cw.id + '"]';
      document.querySelectorAll(sel).forEach(function (img) { img.src = url; });
    }
    requestAnimationFrame(stepColorwayThumb);
  }

  // Materials is no longer its own step. Each shoe simply renders its detected
  // leather/suede mix (restoreDetectedMix), and per-part material is refined in
  // Customize via the leather/suede pills (applyPartMaterial).
  //
  // Restore every body mesh to its detected-mix material from the snapshot
  // captured at load (after MD5 tagging + part-kind overrides). One code path for
  // "the detected mix" so the first slide renders exactly what's saved.
  function restoreDetectedMix(scene) {
    if (!scene) return;
    scene.traverse(function (obj) {
      if (!obj.isMesh || !obj.name) return;
      var snap = liveModel.snapshot[obj.name];
      if (!snap || !snap.map) return;
      P.applyMaterialKind([obj], snap.kind, snap.map, obj.name, snap.props);
    });
  }

  // Lining is no longer its own step — it lives inside Customize (the parts
  // editor). See openLiningSub / renderLiningSub near renderPartsStep. The
  // selectable options exclude "No lining" (every shoe ships with a lining).
  function liningChoices() {
    return D.liningOptions.filter(function (l) { return l.id !== 'none'; });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP SOLE — two feels (Ground / Cloud) + a contextual control
  // ──────────────────────────────────────────────────────────────────────────
  // Two feels, and the feel IS the sole (michael, 2026-10-14: "ground or cloud
  // feel only - and it changes the sole. they still get two footbeds"):
  //   Ground-Feel → idx 1 (two footbeds on the ground sole, recommended),
  //                 idx 0 (warn — footbeds removed, bare ground sole)
  //   Cloud-Feel  → idx 3 (two footbeds on the cushioned sole)
  // idx 2 (cork midsole) is RETIRED from the shop. It stays in the shared
  // schema because orders already sold with it still resolve in the admin;
  // nothing here can select it, and a saved link carrying it lands on Cloud.
  // The active feel is read straight off the position's `sole` field.
  var SOLE_FEELS = [
    { feel: 'ground', name: 'Ground-Feel', rec: true,
      tag: 'Close to the ground · high proprioception',
      feelText: 'Thin, firm rubber that bends with your foot. You feel every texture and grade underfoot — as close to barefoot as a shoe gets.',
      specs: [] }, // filled by groundFeelSpecs()
    { feel: 'cloud', name: 'Cloud-Feel',
      tag: 'Softer underfoot · bounce and cushion',
      feelText: 'Thicker, springier rubber that softens each step and gives a little back. You still feel the ground, just gentler.',
      specs: [] } // filled by cloudFeelSpecs()
  ];

  // The (i) on each sole line: what the material is and why it feels that way.
  var SOLE_TIPS = {
    ground: 'Solid Vibram rubber, 4mm. Dense and grippy but thin enough to flex with every step, so the ground comes through to your foot while sharp things stay out. Hard-wearing, and resoleable on your last.',
    cloud:  'Vibram NewFlex, 8mm. An expanded rubber full of tiny closed air cells, much lighter than solid rubber. It compresses under each step and springs back, which is the cushion. Still flexible, still zero-drop.'
  };
  var FOOTBED_SPEC = { text: '3mm Footbeds (×2): swappable, removable' };

  // The sole a stack position ships on, as the customer reads it (cart line +
  // review). Kept generic on purpose — the exact Vibram article is the shop's
  // choice and changes with stock. The cork branch only names legacy
  // (pre-2026-10-14) builds.
  function soleComboName(pos) {
    if (pos.midsole === 'foam') return 'Vibram NewFlex 8mm';
    if (pos.midsole === 'cork') return 'Vibram NewFlex 4mm + Cork';
    return 'Vibram rubber 4mm';
  }

  function cloudFeelSpecs() {
    return [ FOOTBED_SPEC,
             { text: '8mm Vibram NewFlex sole', tip: SOLE_TIPS.cloud } ];
  }

  function groundFeelSpecs(footbed) {
    return footbed
      ? [ FOOTBED_SPEC,
          { text: '4mm Vibram rubber sole', tip: SOLE_TIPS.ground } ]
      : [ { text: '4mm Vibram rubber sole — nothing between you and the ground', tip: SOLE_TIPS.ground } ];
  }

  // Same (i) + click-to-open bubble as the review step's Size row — the
  // document-level [data-size-info] handler opens/positions/closes it.
  function infoTipHtml(text) {
    return ' <span class="vt-size-info-wrap">'
         +   '<button type="button" class="vt-size-info" aria-expanded="false" aria-label="' + text + '" data-size-info>i</button>'
         +   '<span class="vt-size-tip" role="tooltip">' + text + '</span>'
         + '</span>';
  }

  function soleSpecItemsHtml(specs) {
    return specs.map(function (s) {
      return '<li class="vt-sole-spec">' + s.text + (s.tip ? infoTipHtml(s.tip) : '') + '</li>';
    }).join('');
  }

  function soleFeelCardHtml(f) {
    var specs = f.feel === 'cloud' ? cloudFeelSpecs() : groundFeelSpecs(true);
    return '<div class="vt-card vt-card--lining vt-sole-feel-card" role="button" tabindex="0" data-sole-feel="' + f.feel + '">'
         +   '<div class="vt-card-meta">'
         +     '<span class="vt-card-name">' + f.name + (f.rec ? ' ' + recChip() : '') + '</span>'
         +     '<span class="vt-card-blurb">' + f.tag + '</span>'
         +     '<span class="vt-card-blurb vt-sole-feel-text">' + f.feelText + '</span>'
         +     '<ul class="vt-sole-spec-list" data-sole-specs="' + f.feel + '">'
         +       soleSpecItemsHtml(specs)
         +     '</ul>'
         +   '</div>'
         + '</div>';
  }

  function renderSoleStep() {
    var panel = stepPanel('sole');
    panel.innerHTML = stepHead('sole')
      + '<div class="vt-step-body vt-sole-body">'
      + '<p class="vt-step-lead">Choose how close you want to walk to the ground.</p>'
      + '<div class="vt-grid vt-grid--lining vt-sole-options">'
      +   SOLE_FEELS.map(soleFeelCardHtml).join('')
      + '</div>'
      + '<div class="vt-sole-control" id="vt-sole-control"></div>'
      + '</div>'
      + stepFoot('sole');

    panel.addEventListener('click', function (e) {
      // The (i) inside a card opens its tip; it must not also pick the card.
      if (e.target.closest('[data-size-info]')) return;
      var feelCard = e.target.closest('[data-sole-feel]');
      if (feelCard) {
        var pos = D.soleStackPositions[state.selectedSoleIdx];
        // Switching feel lands on that feel's default (footbeds in, for
        // either sole); staying on the same feel is a no-op.
        if (feelCard.dataset.soleFeel !== pos.sole) {
          setSolePosition(feelCard.dataset.soleFeel === 'ground' ? 1 : 3);
        }
        return;
      }
      var footSeg = e.target.closest('[data-sole-footbed]');
      if (footSeg) { setSolePosition(footSeg.dataset.soleFootbed === 'off' ? 0 : 1); return; }
    });

    refreshSoleCards();
  }

  // Light up the active feel and (re)render the contextual control beneath it.
  function refreshSoleCards() {
    var panel = stepPanel('sole');
    if (!panel) return;
    var pos = D.soleStackPositions[state.selectedSoleIdx];
    panel.querySelectorAll('[data-sole-feel]').forEach(function (card) {
      card.classList.toggle('vt-card--active', card.dataset.soleFeel === pos.sole);
    });
    // The ground card's spec list follows the footbed choice — previews "with
    // footbeds" (the recommended default) when Cloud is active.
    var groundList = panel.querySelector('[data-sole-specs="ground"]');
    if (groundList) groundList.innerHTML = soleSpecItemsHtml(groundFeelSpecs(pos.sole === 'ground' ? pos.footbed : true));
    var ctrl = panel.querySelector('#vt-sole-control');
    // Only Ground has a sub-choice (take the footbeds out); Cloud has none.
    if (ctrl) ctrl.innerHTML = (pos.sole === 'ground') ? groundControlHtml() : '';
  }

  // ── Welt / No welt ─────────────────────────────────────────────────────────
  // Any welted shoe can be built without its welt (same price). The choice is
  // an On / Off pill pair on the Welt card in Customize, styled exactly like the
  // Leather / Suede pills on the body parts (the Sole step only previews the
  // welt, fading it in and out). No welt hides the welt and the stitching that
  // sews it (window.cfApplyWelt, shared with the product page) and writes
  // Welt: None to the cart line; the Welt card stays in the strip (Off lit) so
  // it can be switched back on. Desert has no welt, so it never asks.
  function shoeHasWelt() {
    var id = state.selectedShoe && state.selectedShoe.id;
    return w.cfShoeHasWelt ? w.cfShoeHasWelt(id) : id !== 'desert';
  }
  function weltOn() { return state.welt && shoeHasWelt(); }

  function isWeltGroup(g) { return w.VitrineKit.isWeltGroup(g); }   // shared with the product page

  // On / Off pills for the Welt card — the same markup and classes as the
  // Leather / Suede pills (materialPillsHtml), so they read as one control.
  function weltPillsHtml() {
    var on = weltOn();
    return '<div class="vt-parts-mat vt-parts-mat--welt" role="group" aria-label="Welt">'
         +   '<button type="button" class="vt-parts-mat-btn' + (on ? ' vt-parts-mat-btn--active' : '') + '"'
         +     ' data-welt-btn="on" aria-pressed="' + on + '">On</button>'
         +   '<button type="button" class="vt-parts-mat-btn' + (!on ? ' vt-parts-mat-btn--active' : '') + '"'
         +     ' data-welt-btn="off" aria-pressed="' + !on + '">Off</button>'
         + '</div>';
  }

  // With the welt off its meshes are hidden and dropped from liveGroups, so
  // the strip gets a stand-in Welt card carrying the pills (Off lit).
  function weltOffRowHtml() {
    return '<div class="vt-parts-row vt-parts-row--welt-off" data-welt-row>'
         + '<div class="vt-parts-thumb-wrap"><span class="vt-parts-welt-off-art" aria-hidden="true"></span></div>'
         + '<div class="vt-parts-info">'
         +   '<div class="vt-parts-foot">'
         +     '<span class="vt-parts-dot vt-parts-dot--none"></span>'
         +     '<span class="vt-parts-name">Welt</span>'
         +   '</div>'
         +   weltPillsHtml()
         + '</div>'
         + '</div>';
  }

  function setWelt(on) {
    if (on === state.welt) return;
    state.welt = on;
    if (viewer && viewer.getModelScene && viewer.getModelScene()) {
      repaintReview();
      if (state.currentStep === 'parts') {
        // The welt's own row disappears with it — close its swatches.
        if (activePartId && !liveGroups.some(function (g) { return g.id === activePartId; })) {
          activePartId = null;
          var sw = document.getElementById('vt-parts-swatches-wrap');
          if (sw) sw.hidden = true;
        }
        renderPartsStrip();
        refreshPartsThumbs();
      }
    }
  }

  // Groups minus hidden meshes (the welt + its stitching when built without
  // one), so no row, summary line or spec zone describes a part that isn't
  // on the shoe. Groups left empty are dropped.
  function dropHiddenMeshes(groups) { return K.dropHiddenMeshes(groups); }

  // Ground-Feel: a discreet Footbeds / No Footbeds toggle. Footbeds is the
  // recommended default; choosing No Footbeds drops to idx 0 and fires the
  // no-footbed warning.
  function groundControlHtml() {
    var withFootbeds = state.selectedSoleIdx !== 0; // idx 0 = no footbeds
    return '<div class="vt-sole-toggle" role="group" aria-label="Footbeds">'
         +   '<button type="button" class="vt-sole-seg' + (withFootbeds ? ' vt-sole-seg--active' : '') + '" data-sole-footbed="on">'
         +     'Footbeds ' + recChip()
         +   '</button>'
         +   '<button type="button" class="vt-sole-seg' + (!withFootbeds ? ' vt-sole-seg--active' : '') + '" data-sole-footbed="off">'
         +     'No Footbeds'
         +   '</button>'
         + '</div>';
  }

  function setSolePosition(idx, opts) {
    opts = opts || {};
    var prevIdx = state.selectedSoleIdx;
    state.selectedSoleIdx = idx;
    var pos = D.soleStackPositions[idx];
    refreshSoleCards();
    if (!opts.silent && prevIdx !== idx) {
      if (!pos.footbed) openWarning('noFootbed');
    }
    if (!opts.silent && D.stepsWithCanvas.indexOf(state.currentStep) !== -1) ensureLiveScene();
  }

  // One reusable warning popup (same modal, same code path) — only the title +
  // body swap.
  var SOLE_WARNINGS = {
    noFootbed: {
      title: 'Nothing between you and the ground.',
      body:  'No footbeds means the most direct contact with the ground — every texture, every grade, unfiltered. If you’re new to barefoot-style walking, footbeds are the easier place to start.'
    }
  };
  function openWarning(kind) {
    var m = document.getElementById('vt-footbed-warn');
    if (!m) return;
    var w = SOLE_WARNINGS[kind] || SOLE_WARNINGS.noFootbed;
    var t = m.querySelector('.vt-warn-title'); if (t) t.textContent = w.title;
    var b = m.querySelector('.vt-warn-body');  if (b) b.textContent = w.body;
    m.classList.remove('vt-hidden');
  }
  function closeFootbedWarning() {
    var m = document.getElementById('vt-footbed-warn');
    if (m) m.classList.add('vt-hidden');
  }

  // ── STEP PART PULSE ─────────────────────────────────────────────────────────
  // Slowly pulse the part a step is about: the sole on Sole, the drawn lining on
  // Lining — so it's obvious in 3D what the choice affects.
  var stepPulseTimer = null;
  function stopStepPulse() {
    if (stepPulseTimer) { clearInterval(stepPulseTimer); stepPulseTimer = null; }
    if (viewer && viewer.cancelFlash) viewer.cancelFlash();
    stopWeltFade();
  }
  // The lining we draw is the inside layer of each part — mirror the painter's
  // forEachInsideMesh (group by base, pick the inside mesh) so we flash exactly
  // what applyLining restyled.
  function collectLiningMeshes(scene) { return w.VitrineKit.liningMeshes(scene, P); }   // shared with the product page
  // Sole step: the welt breathes — fades to near-transparent and back — while
  // the sole flashes, so the two read as separate parts. Opacity is driven on
  // whatever material each welt mesh holds this frame (a repaint mid-step
  // swaps in fresh clones), and every material touched goes back to its
  // mesh's original transparent / opacity / depthWrite on stop.
  var weltFade = null;
  function startWeltFade() {
    stopWeltFade();
    var baseByMesh = new Map(), baseByMat = new Map();
    var job = { raf: 0, t0: performance.now() };
    function frame(now) {
      var scene = viewer && viewer.getModelScene && viewer.getModelScene();
      var meshes = (scene && weltOn() && P.weltMeshes) ? P.weltMeshes(scene) : [];
      // 0.12 ↔ 1 over 2.8 s, starting fully opaque.
      var a = 0.56 + 0.44 * Math.cos((now - job.t0) / 2800 * Math.PI * 2);
      meshes.forEach(function (m) {
        if (m.visible === false) return;
        var mats = Array.isArray(m.material) ? m.material : [m.material];
        if (!baseByMesh.has(m) && mats[0]) {
          baseByMesh.set(m, { transparent: mats[0].transparent, opacity: mats[0].opacity, depthWrite: mats[0].depthWrite });
        }
        var base = baseByMesh.get(m);
        mats.forEach(function (mat) {
          if (!mat) return;
          if (!baseByMat.has(mat)) baseByMat.set(mat, base);
          if (!mat.transparent) { mat.transparent = true; mat.needsUpdate = true; }
          mat.opacity = base.opacity * a;
          mat.depthWrite = a > 0.95;
        });
      });
      job.raf = requestAnimationFrame(frame);
    }
    job.raf = requestAnimationFrame(frame);
    job.restore = function () {
      baseByMat.forEach(function (base, mat) {
        mat.opacity = base.opacity;
        mat.depthWrite = base.depthWrite;
        if (mat.transparent !== base.transparent) { mat.transparent = base.transparent; mat.needsUpdate = true; }
      });
    };
    weltFade = job;
  }
  function stopWeltFade() {
    if (!weltFade) return;
    cancelAnimationFrame(weltFade.raf);
    weltFade.restore();
    weltFade = null;
  }

  function startStepPulse(slug) {
    stopStepPulse();
    if ((slug !== 'sole' && slug !== 'lining') || !viewer || !viewer.flashPart) return;
    if (slug === 'sole') startWeltFade();
    function targetGroup() {
      if (slug === 'sole') {
        // The sole unit itself (the separately-loaded sole GLB) plus any sole
        // meshes in the upper — never the welt, which is fading instead.
        var meshes = viewer.getSoleMeshes ? viewer.getSoleMeshes().slice() : [];
        liveGroups.forEach(function (g) {
          if (g.isSole && !isWeltGroup(g)) meshes = meshes.concat(g.meshes);
        });
        return meshes.length ? { meshes: meshes, isSole: false } : null;
      }
      // lining: nothing drawn when 'No lining' is selected
      if (!state.selectedLining || state.selectedLining.id === 'none') return null;
      var lm = collectLiningMeshes(viewer.getModelScene());
      return lm.length ? { meshes: lm, isSole: false } : null;
    }
    function pulse() { var g = targetGroup(); if (g) viewer.flashPart(g); }
    pulse();
    stepPulseTimer = setInterval(pulse, 1400);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP REVIEW — 3D viewer + summary + action chips
  // ──────────────────────────────────────────────────────────────────────────
  function renderReviewStep() {
    var panel = stepPanel('review');
    // Review is intentionally minimal: Add to cart / Add a note / Share (all in
    // the cart row). Changing the design, colours, lining, sole, or parts is done
    // by jumping to those steps from the progress nav (top-right) — Customize is
    // now a step of its own, so there's no "Edit parts" button here either.
    panel.innerHTML = stepHead('review')
      + '<div class="vt-step-body vt-review-body">'
      +   '<div class="vt-cart-row" id="vt-cart-row"></div>'
      +   '<dl class="vt-summary" id="vt-summary"></dl>'
      + '</div>';

    panel.addEventListener('click', function (e) {
      if (e.target.closest('[data-take-photos]')) { e.preventDefault(); handleTakePhotos(); return; }
      var editBtn = e.target.closest('#vt-edit-steps');
      if (editBtn) { openStepsPopup(editBtn); return; } // same popup as the status circle
      if (e.target.closest('#vt-add-note'))    openNotesModal();
      if (e.target.closest('#vt-edit-in-cart'))updateCartLine();
      if (e.target.closest('#vt-add-to-cart')) addToCart();
      if (e.target.closest('#vt-share'))       shareLink();
    });

    renderCartActions();
  }

  // Heading to the scan page loses the in-progress build, so if the shoe isn't in
  // the cart yet we prompt to save it first (it can be edited later). Once it's in
  // the cart — freshly added, or reopened from a cart line — we go straight there.
  // Triggered by the "(take photos)" anchor in the Size row.
  function handleTakePhotos() {
    if (savedInCart()) {
      location.href = '/pages/scan';
    } else {
      openScanSaveModal();
    }
  }
  function openScanSaveModal() {
    var m = document.getElementById('vt-scan-save-modal');
    if (m) m.classList.remove('vt-hidden');
  }

  // The cart buttons depend on whether we reopened an existing cart line. When
  // editing one that's still in the cart, offer "Edit shoe in cart" (update in
  // place) alongside "Add new shoe to cart"; otherwise just "Add to cart". Taking
  // photos is the small anchor in the Size row, not an action button here.
  function renderCartActions() {
    var row = document.getElementById('vt-cart-row');
    if (!row) return;
    var editing = state.editingLineKey && state.editLineInCart;
    row.classList.toggle('vt-cart-row--editing', !!editing);
    row.innerHTML = editing
      ? '<button class="vt-btn vt-btn--primary" id="vt-edit-in-cart">Edit shoe in cart</button>'
        + '<button class="vt-btn" id="vt-add-to-cart">Add new shoe to cart</button>'
        + '<button class="vt-btn-ghost" id="vt-add-note">Add a note</button>'
        + '<button class="vt-btn-ghost" id="vt-share">Share</button>'
      : '<button class="vt-btn vt-btn--primary" id="vt-add-to-cart">Add to cart</button>'
        + '<button class="vt-btn-ghost vt-cart-edit" id="vt-edit-steps">Edit</button>'
        + '<button class="vt-btn-ghost" id="vt-add-note">Add a note</button>'
        + '<button class="vt-btn-ghost" id="vt-share">Share</button>';
    if (beta) beta.renderCart();
  }

  // Confirm the reopened line still exists in the cart (the customer may have
  // removed it), then render the appropriate cart buttons.
  function refreshCartActions() {
    if (!state.editingLineKey) { state.editLineInCart = false; renderCartActions(); return; }
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        state.editLineInCart = (cart.items || []).some(function (it) { return it.key === state.editingLineKey; });
      })
      .catch(function () { state.editLineInCart = false; })
      .then(function () { renderCartActions(); });
  }

  function initViewer() {
    if (viewer) return;
    var canvas = document.getElementById('vt-canvas');
    if (!canvas) return;
    viewer = V.create({ canvas: canvas, container: canvas.parentNode });
    // 3D click → pick the part under the cursor; only used in the parts step.
    canvas.addEventListener('click', function (e) {
      if (beta && state.currentStep === 'build') {
        var hitMesh = viewer.pickMeshAtClick(e);
        beta.onPick(hitMesh ? findGroupForMesh(hitMesh) : null);
        return;
      }
      if (state.currentStep !== 'parts') return;
      var mesh = viewer.pickMeshAtClick(e);
      if (!mesh) return;
      var group = findGroupForMesh(mesh);
      if (group) selectPart(group.id, { scrollIntoView: true });
    });
  }

  // Single entry point for loading/repainting the live scene. Shoe and sole are
  // tracked separately — swapping just the sole doesn't reload the upper.
  var loadedShoeId   = null;
  var loadedShoeGender = null;
  var loadedSoleValue = null;

  function ensureLiveScene() {
    if (!viewer) return;
    var shoe = state.selectedShoe;
    if (!shoe.glb) return;
    // Female shapes still loading in the background — build the scene once
    // they land (needs their bytes + kind map, not a second download).
    if (state.shoeGender === 'female' && femaleSet.state !== 'ready') {
      if (!ensureLiveScene.waiting) {
        ensureLiveScene.waiting = true;
        whenGenderReady('female', function () { ensureLiveScene.waiting = false; ensureLiveScene(); });
      }
      return;
    }
    var pos = D.soleStackPositions[state.selectedSoleIdx];

    var shoeChanged = loadedShoeId !== shoe.id || loadedShoeGender !== state.shoeGender;
    var soleChanged = loadedSoleValue !== pos.sole;

    if (!shoeChanged && !soleChanged && viewer.getModelScene()) {
      // Same shoe + same sole — just re-apply colour / lining changes.
      repaintReview();
      return;
    }

    if (shoeChanged) {
      // New shoe / new gender → full reload (model + sole, paint, spin).
      loadedShoeId     = shoe.id;
      loadedShoeGender = state.shoeGender;
      loadedSoleValue  = pos.sole;
      // Regenerate from the bytes fetched at preload — no second download.
      var preBuf = bufferSet(state.shoeGender)[shoe.id];
      viewer.loadModel(shoeUrl(shoe.id), function (err, ms, gltf) {
        if (err) { loadedShoeId = null; return; }
        // Tag with the per-shoe MD5 → kind map cached during detection.
        var buf = bufferSet(state.shoeGender)[shoe.id];
        var md5Map = md5MapSet(state.shoeGender)[shoe.id];
        // A baked GLB parses with its kinds already on the materials.
        if (gltf && buf && md5Map && !(P.isKindBaked && P.isKindBaked(gltf))) {
          P.tagMaterialKindsFromGltf(gltf, buf, md5Map);
          P.tagMaterialKindsByMaterialIndex(gltf, buf, md5Map);
        }
        if (ms) P.normalizeLaces(ms);
        captureLiveModel(ms); // native per-kind sources + snapshot, before any swap
        applyPartKindOverrides(ms, shoe.id); // e.g. surplus aglet → suede
        restoreDetectedMix(ms); // render the detected mix identically to the Materials "Mixed" card

        viewer.loadSole(currentSoleModels()[pos.sole], function () {
          repaintReview();
          viewer.spinOnLoad(2400);
          if (state.currentStep === 'parts') refreshPartsThumbs();
        });
      }, preBuf ? { buffer: preBuf } : null);
      return;
    }

    // Sole-only change → swap sole, keep upper exactly as-is. Sync the new
    // sole's side meshes to the current welt color so the seam stays clean.
    loadedSoleValue = pos.sole;
    viewer.loadSole(currentSoleModels()[pos.sole], function () {
      var c = P.classifyGroups(liveGroups);
      var welt = P.findGroupByKeyword(liveGroups, 'welt') || c.sole;
      var sideMeshes = viewer.getSoleSideMeshes();
      var sideColor = (welt && welt.lastColor) ? welt.lastColor.color
        : (function (cw) { return cw && cw.sole && cw.sole.color; })(shoeColorway(state.selectedColorway, state.selectedShoe.id));
      if (sideColor && sideMeshes.length) P.paintMeshes(sideMeshes, sideColor);
      viewer.reflowFloor(); // rebuild the mirrored partner with the new sole
    });
  }

  function findGroupForMesh(mesh) {
    for (var i = 0; i < liveGroups.length; i++) {
      var g = liveGroups[i];
      for (var j = 0; j < g.meshes.length; j++) {
        if (g.meshes[j] === mesh) return g;
      }
    }
    return null;
  }

  // Feed the generative backdrop the shoe's actual current colours so it tracks
  // every colourway pick, material swap, and per-part edit — not just slide 1.
  function updateBackdropFromShoe() {
    if (!viewer || !viewer.setBackdropHex) return;
    // Collect the DISTINCT colours actually on the shoe (every part), then top up
    // from the colourway — so the backdrop always has three varied tones to flow
    // between instead of collapsing to one flat colour.
    var distinct = [];
    function add(c) {
      if (!c) return;
      var u = c.toUpperCase();
      if (distinct.indexOf(u) === -1) distinct.push(u);
    }
    if (liveGroups && liveGroups.length) {
      liveGroups.forEach(function (g) { if (g.lastColor) add(g.lastColor.color); });
    }
    var cw = state.selectedColorway || {};
    [cw.body1, cw.body2, cw.sole, cw.laces, cw.stitch].forEach(function (s) { if (s) add(s.color); });
    var a = distinct[0], b = distinct[1] || distinct[0], c = distinct[2] || distinct[1] || distinct[0];
    viewer.setBackdropHex(a, b, c);
  }

  // Paint a model's groups for a colorway. Most shoes use the painter's generic
  // body1/body2 split; Desert and City have explicit per-part rules (by mesh
  // name, so the live shoe and its thumbnail always agree). The rules live in
  // window.cfPaintShoeColorway (snippets/cf-vitrine-catalog-patch.liquid),
  // shared with the carousel and the product-page 3D.
  function paintColorwayForShoe(groups, cw, shoeId) {
    if (window.cfPaintShoeColorway) { window.cfPaintShoeColorway(P, groups, cw, shoeId); return; }
    P.applyColorway(groups, cw);
  }
  function shoeColorway(cw, shoeId) {
    return window.cfShoeColorway ? window.cfShoeColorway(cw, shoeId) : cw;
  }

  function repaintReview() {
    var modelScene = viewer.getModelScene();
    if (!modelScene) return;

    // Restore any in-progress flash before we clone/paint — otherwise the flash's
    // white emissive gets baked into the new material instances and sticks.
    if (viewer.cancelFlash) viewer.cancelFlash();
    if (beta) beta.beforeRepaint();   // same for the beta's glow

    // 0. Welt on/off — hide the welt and its stitching before grouping, so
    // neither turns up as a part row or gets painted.
    if (w.cfApplyWelt) w.cfApplyWelt(modelScene, weltOn());

    liveGroups = K.partGroups(modelScene, state.partsMode);

    // 1. Apply colorway
    paintColorwayForShoe(liveGroups, state.selectedColorway, state.selectedShoe.id);

    // 2. Apply welt → sole side sync. Falls back to colorway.sole when the model
    // has no welt/sole mesh, so the separately-loaded sole never stays unpainted.
    var welt = P.findGroupByKeyword(liveGroups, 'welt') || P.classifyGroups(liveGroups).sole;
    var sideMeshes = viewer.getSoleSideMeshes();
    if (sideMeshes.length) {
      var soleColor = (welt && welt.lastColor)
        ? welt.lastColor.color
        : (function (cw) { return cw && cw.sole && cw.sole.color; })(shoeColorway(state.selectedColorway, state.selectedShoe.id));
      if (soleColor) P.paintMeshes(sideMeshes, soleColor);
    }

    // 3. Apply per-part overrides BY BUCKET. Each mesh resolves its override
    // from its colorBucket (anatomical prefix, or a shared laces/stitch/sole/
    // welt bucket), so:
    //   • Advanced edits survive a switch to Simple — three prefixes painted
    //     three colours stay distinct even when merged into one Simple body
    //     group (each mesh still resolves to its own prefix's colour).
    //   • A restored/shared build re-expands from one saved representative per
    //     bucket, colouring every layer mesh of the part (not just the one).
    // 3b. …then leather/suede swaps by bucket (after paintMeshes clones the
    // materials). Same code as the product page (VitrineKit.applyOverrides).
    K.applyOverrides(liveGroups, {
      colors: state.partColorsByMeshName,
      mats: preload.referenceTextures ? state.partMaterialsByMeshName : null,
      kindSwapSource: kindSwapSource,
      sideMeshes: sideMeshes
    });

    // 4. Lining
    var profile = D.liningMaterials[state.selectedLining.id] || null;
    P.applyLining(modelScene, viewer.getLiningSnapshot(), state.selectedLining, profile, D.liningInsetMetres);

    // 5. Simple-mode re-bucket. After all paints land, REGROUP every body
    // mesh by (cfKind, current colour) so:
    //   - Multiple texture instances of the same kind collapse into one row
    //     (the screenshot complaint: 3 "Suede" rows for one shoe).
    //   - Advanced edits surface as distinct rows whenever they change colour
    //     within a kind (e.g. "Leather · Burgundy" and "Leather · Cream").
    liveGroups = K.finishGroups(liveGroups, state.partsMode);

    viewer.reflowFloor();

    // Tint the generative backdrop to the shoe's CURRENT colours.
    updateBackdropFromShoe();

    // Summary's Colors row reads liveGroups.lastColor, so refresh it whenever
    // a repaint changes the on-screen colours.
    if (state.currentStep === 'review' && document.getElementById('vt-summary')) {
      renderSummary();
    }
    if (beta) beta.onRepaint();
  }

  // Re-bucket simple-mode body groups by (cfKind, current colour). Specials
  // (sole, laces, welt, stitching) pull out of the input and append at the
  // end in a fixed order: sole, then laces → welt → stitching (the trio the
  // user wants pinned to the end). Bodies collapse to one row per kind ×
  // colour: three texture-instance suede groups all painted the same colour
  // become one "Suede" row; advanced edits that introduced a second colour on
  // the leather meshes produce "Leather · <colour>" rows.
  // Advanced mode keeps each part as its own prefix group, but the specials
  // must trail the bodies in the SAME order Simple uses: bodies, then
  // sole → laces → welt → stitching. Bodies keep their existing order.
  function orderSpecialsToEnd(groups) { return K.orderSpecialsToEnd(groups); }

  function regroupSimpleBodies(groups) { return K.regroupSimpleBodies(groups); }

  function renderSummary() {
    var pos = D.soleStackPositions[state.selectedSoleIdx];
    // Size is always "Perfect" — the pair is cut to the customer's own foot. The
    // link runs the same "save to cart first?" flow as the Take photos button;
    // label depends on whether they've measured yet. The (i) reminds them the
    // build is based on their measurements.
    var hasScan = !!(w.cfHasScan && w.cfHasScan());
    var sizeLink = hasScan ? '(view foot measurements)' : '(take photos)';
    var sizeMsg = 'This shoe will be built for you based on your exact dimensions.';
    var sizeVal =
        'Perfect'
      + ' <span class="vt-size-info-wrap">'
      +   '<button type="button" class="vt-size-info" aria-expanded="false"'
      +     ' aria-label="' + sizeMsg + '" data-size-info>i</button>'
      +   '<span class="vt-size-tip" role="tooltip">' + sizeMsg + '</span>'
      + '</span>'
      + ' <a class="vt-size-link" href="/pages/scan" data-take-photos'
      +     ' title="We’ll offer to save your shoe to the cart first, so you can come back and edit it.">' + sizeLink + '</a>'
      + '<span class="vt-size-warn">You can always take your photos after adding to cart.</span>';
    var rows = [
      ['Size',      sizeVal],
      ['Design',    state.selectedShoe.name],
      ['Upper',     upperSummary()],
      ['Lining',    state.selectedLining.name],
      ['Stack',     pos.label + ' · ' + pos.totalMm + 'mm'],
      ['Sole',      soleComboName(pos)],
      ['Footbed',   pos.footbed ? '1× Cork, 1× Foam' : 'None']
    ];
    if (shoeHasWelt()) rows.push(['Welt', weltOn() ? 'Yes' : 'None']);
    document.getElementById('vt-summary').innerHTML = rows.map(function (r) {
      return '<dt>' + r[0] + '</dt><dd>' + r[1] + '</dd>';
    }).join('');
  }

  // The upper as "Colour Material" pairs, one per distinct body zone (laces,
  // stitching and the sole/welt are not part of the upper, so they're excluded).
  // Reads liveGroups (colour after part overrides + the cfKind material tag);
  // falls back to the colourway + material mode before the live scene loads.
  function upperSummary() {
    if (liveGroups && liveGroups.length) {
      var seen = {}, items = [];
      liveGroups.forEach(function (g) {
        if (g.isLaces || g.isStitch || g.isSole) return;
        var lc = g.lastColor;
        var colorLabel = lc && (lc.label || D.hexLabel[(lc.color || '').toUpperCase()] || lc.color);
        var kind = P.getGroupMaterialKind(g);
        var kindLabel = kind ? (kind.charAt(0).toUpperCase() + kind.slice(1)) : '';
        var pair = [colorLabel, kindLabel].filter(Boolean).join(' ');
        if (pair && !seen[pair]) { seen[pair] = true; items.push(pair); }
      });
      if (items.length) return items.join(', ');
    }
    var mat = state.materialMode === 'leather' ? 'Leather'
            : state.materialMode === 'suede'   ? 'Suede' : 'Leather & Suede';
    return state.selectedColorway.label + ' · ' + mat;
  }

  // Distinct materials currently on the shoe's body (leather / suede), read from
  // the live scene's cfKind tags so it reflects the detected mix plus any
  // Materials-step or per-part swaps. Falls back to the global materialMode
  // before the live scene has finished loading. Powers the review summary and
  // the saved cart properties (the workshop tech sheet).
  function materialsSummary() {
    if (liveGroups && liveGroups.length) {
      var seen = {}, labels = [];
      liveGroups.forEach(function (g) {
        if (g.isLaces || g.isStitch || g.isSole) return;
        var kind = P.getGroupMaterialKind(g);
        if (!kind) return;
        var label = kind.charAt(0).toUpperCase() + kind.slice(1);
        if (!seen[label]) { seen[label] = true; labels.push(label); }
      });
      if (labels.length) return labels.join(', ');
    }
    if (state.materialMode === 'leather') return 'Leather';
    if (state.materialMode === 'suede')   return 'Suede';
    return 'Leather, Suede';
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PARTS — its own slide. Parts strip on the left (big cards with thumbnails);
  // swatches appear beneath when a part is selected. The 3D shoe is the global
  // canvas frame on the right.
  // ──────────────────────────────────────────────────────────────────────────
  var activePartId = null;

  function renderPartsStep() {
    var panel = stepPanel('parts');
    panel.innerHTML = stepHead('parts')
      + '<div class="vt-step-body vt-parts-body">'
      +   '<div class="vt-parts-main" id="vt-parts-main">'
      +     '<div class="vt-rail">'
      +       '<button class="vt-carousel-arrow vt-carousel-arrow--prev" type="button" data-rail="prev" aria-label="Previous parts">‹</button>'
      +       '<div class="vt-parts-list vt-scroll-x" id="vt-parts-list"></div>'
      +       '<button class="vt-carousel-arrow vt-carousel-arrow--next" type="button" data-rail="next" aria-label="More parts">›</button>'
      +     '</div>'
      +     '<div class="vt-parts-swatches-wrap" id="vt-parts-swatches-wrap" hidden>'
      +       '<div class="vt-swatches" id="vt-parts-swatches"></div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="vt-lining-sub vt-hidden" id="vt-lining-sub"></div>'
      + '</div>'
      + stepFoot('parts');

    renderSwatches();
    bindRailArrows(panel.querySelector('.vt-rail'));
    buildPartsPop();   // settings popup lives outside the transform-animated panel

    panel.addEventListener('click', function (e) {
      // Lining sub-panel (opened from the "Lining" row at the end of the strip).
      if (e.target.closest('[data-lining-row]'))  { openLiningSub();  return; }
      if (e.target.closest('[data-lining-back]')) { closeLiningSub(); return; }
      var linCard = e.target.closest('[data-lining-id]');
      if (linCard) { selectLining(linCard.dataset.liningId); return; }

      if (e.target.closest('[data-parts-settings]'))       { openPartsPop(); return; }
      var weltBtn = e.target.closest('[data-welt-btn]');
      if (weltBtn) { setWelt(weltBtn.dataset.weltBtn === 'on'); return; }
      if (e.target.closest('[data-welt-row]')) return; // stand-in card: only its pills act
      var matBtn = e.target.closest('[data-material-btn]');
      if (matBtn) { applyPartMaterial(matBtn.dataset.groupId, matBtn.dataset.kind); return; }
      var row = e.target.closest('.vt-parts-row[data-group-id]');
      if (row) { selectPart(row.dataset.groupId); return; }
      var sw  = e.target.closest('.vt-swatch');
      if (sw && activePartId) {
        var swatch = D.swatchPalette[parseInt(sw.dataset.swatchIdx, 10)];
        if (swatch) paintActivePart(swatch);
      }
    });
  }

  // ── Lining sub-panel — lives inside Customize, opened from the strip ─────────
  // Pops open over the parts list, swings the camera into the topline so the
  // lining is visible, and swings back to the parts framing on the way out. No
  // colour swatches here — lining colour is fixed per material.
  function openLiningSub() {
    var sub  = document.getElementById('vt-lining-sub');
    var main = document.getElementById('vt-parts-main');
    if (!sub || !main) return;
    renderLiningSub();
    main.classList.add('vt-hidden');
    sub.classList.remove('vt-hidden');
    if (viewer && viewer.focusView) viewer.focusView('lining');
    startStepPulse('lining'); // flash the drawn lining so it's obvious what changes
  }

  function closeLiningSub() {
    var sub  = document.getElementById('vt-lining-sub');
    var main = document.getElementById('vt-parts-main');
    stopStepPulse();
    if (sub)  sub.classList.add('vt-hidden');
    if (main) main.classList.remove('vt-hidden');
    if (viewer && viewer.focusView) viewer.focusView('parts');
  }

  // Hide the sub-panel without touching the camera — used when leaving Customize
  // entirely (the destination step sets its own framing).
  function resetLiningSub() {
    var sub  = document.getElementById('vt-lining-sub');
    var main = document.getElementById('vt-parts-main');
    if (sub)  sub.classList.add('vt-hidden');
    if (main) main.classList.remove('vt-hidden');
  }

  function renderLiningSub() {
    var sub = document.getElementById('vt-lining-sub');
    if (!sub) return;
    sub.innerHTML =
        '<div class="vt-lining-sub-head">'
      +   '<p class="vt-eyebrow">Lining</p>'
      + '</div>'
      + '<p class="vt-step-lead">The inner surface that wraps the top and sides of your foot — the whole inside of the shoe except the footbed you stand on.</p>'
      + '<div class="vt-grid vt-grid--lining" id="vt-lining-grid">'
      +   liningChoices().map(function (lo) {
            var active = lo.id === state.selectedLining.id ? ' vt-card--active' : '';
            var isRec  = lo.id === 'leather' || /recommended/i.test(lo.name);
            var name   = lo.name.replace(/\s*\(recommended\)/i, '');
            return '<div class="vt-card vt-card--lining' + active + '" role="button" tabindex="0" data-lining-id="' + lo.id + '">'
                 + '<div class="vt-card-art vt-lining-art vt-lining-art--' + lo.id + '"></div>'
                 + '<div class="vt-card-meta">'
                 +   '<span class="vt-card-name">' + name + (isRec ? ' ' + recChip() : '') + '</span>'
                 +   '<span class="vt-card-blurb">' + lo.blurb + '</span>'
                 + '</div>'
                 + '</div>';
          }).join('')
      + '</div>'
      + '<div class="vt-lining-sub-foot">'
      +   '<button class="vt-btn-ghost" data-lining-back>← Back to parts</button>'
      +   '<button class="vt-btn vt-btn--primary" data-lining-back>Done</button>'
      + '</div>';
  }

  function selectLining(id) {
    var lo = liningChoices().find(function (l) { return l.id === id; });
    if (!lo || lo.id === state.selectedLining.id) {
      // Re-mark anyway in case the active class drifted, but skip the repaint.
      if (lo) state.selectedLining = lo;
    } else {
      state.selectedLining = lo;
      if (D.stepsWithCanvas.indexOf(state.currentStep) !== -1) ensureLiveScene();
    }
    var sub = document.getElementById('vt-lining-sub');
    if (sub) sub.querySelectorAll('.vt-card--lining').forEach(function (c) {
      c.classList.toggle('vt-card--active', c.dataset.liningId === state.selectedLining.id);
    });
    updateLiningRow();
  }

  // Keep the strip's "Lining" row in sync with the current choice (swatch + name).
  function updateLiningRow() {
    var row = document.querySelector('[data-lining-row]');
    if (!row) return;
    var sw = row.querySelector('.vt-parts-lining-swatch');
    if (sw) sw.className = 'vt-parts-lining-swatch vt-lining-art vt-lining-art--' + state.selectedLining.id;
    var subEl = row.querySelector('.vt-parts-sub');
    if (subEl) subEl.textContent = state.selectedLining.name.replace(/\s*\(recommended\)/i, '');
  }

  // The settings popup is a fixed, full-screen centred overlay, so it must live
  // OUTSIDE a .vt-step. Those panels are translateX-animated, which makes them the
  // containing block for position:fixed and traps the popup inside the panel
  // (centred on the settings card at the panel's edge, half-hidden under the
  // canvas). Built once; a rebuild replaces any prior copy.
  function buildPartsPop() {
    var old = document.getElementById('vt-parts-pop');
    if (old) old.remove();
    var pop = document.createElement('div');
    pop.className = 'vt-modal vt-hidden';
    pop.id = 'vt-parts-pop';
    pop.setAttribute('aria-label', 'Parts settings');
    pop.innerHTML =
        '<div class="vt-modal-card vt-parts-pop-card">'
      +   '<header class="vt-modal-head">'
      +     '<p class="vt-eyebrow">Settings</p>'
      +     '<button class="vt-modal-close" data-parts-settings-close aria-label="Close">&times;</button>'
      +   '</header>'
      +   '<div class="vt-parts-pop-opts">'
      +     '<button class="vt-parts-pop-opt vt-parts-pop-opt--active" data-parts-mode="simple">'
      +       '<span class="vt-parts-pop-opt-name">Simple</span>'
      +       '<span class="vt-parts-pop-opt-desc">Colour grouped areas — fewer, larger parts.</span>'
      +     '</button>'
      +     '<button class="vt-parts-pop-opt" data-parts-mode="advanced">'
      +       '<span class="vt-parts-pop-opt-name">Advanced</span>'
      +       '<span class="vt-parts-pop-opt-desc">Every individual panel, on its own.</span>'
      +     '</button>'
      +   '</div>'
      +   '<button class="vt-link vt-parts-reset" data-parts-reset>Reset all parts</button>'
      + '</div>';
    // Sit beside the other modals (siblings of #vt-root, outside the animated
    // panels) so it shares their proven stacking context.
    var host = (document.getElementById('vt-root') || {}).parentNode || document.body;
    host.appendChild(pop);
    pop.addEventListener('click', function (e) {
      if (e.target.closest('[data-parts-settings-close]')) { closePartsPop(); return; }
      if (e.target === pop)                                { closePartsPop(); return; } // backdrop
      var mode = e.target.closest('[data-parts-mode]');
      if (mode) { handlePartsModeClick(mode); closePartsPop(); return; }
      if (e.target.closest('[data-parts-reset]')) { resetPartOverrides(); closePartsPop(); return; }
    });
  }

  function openPartsPop()  { var p = document.getElementById('vt-parts-pop'); if (p) p.classList.remove('vt-hidden'); }
  function closePartsPop() { var p = document.getElementById('vt-parts-pop'); if (p) p.classList.add('vt-hidden'); }

  function handlePartsModeClick(btn) {
    var pop = document.getElementById('vt-parts-pop');
    if (pop) pop.querySelectorAll('[data-parts-mode]').forEach(function (b) {
      b.classList.toggle('vt-parts-pop-opt--active', b === btn);
    });
    var lbl = document.getElementById('vt-parts-mode-label');
    if (lbl) lbl.textContent = btn.dataset.partsMode === 'advanced' ? 'Advanced' : 'Simple';
    switchPartsMode(btn.dataset.partsMode);
  }

  // First time we enter the parts step (after the live scene is ready), build the
  // strip + queue up thumbnails. Subsequent entries reuse cached thumbs.
  function onEnterPartsStep() {
    if (!liveGroups.length) {
      // Scene not yet painted (e.g. user jumped here before review loaded). Try later.
      setTimeout(onEnterPartsStep, 200);
      return;
    }
    resetLiningSub();  // always land on the parts list, never a stale lining sub-panel
    renderPartsStrip();
    refreshPartsThumbs();
    bindWheelHorizontal(document.getElementById('vt-parts-list'));
  }

  function renderPartsStrip() {
    var list = document.getElementById('vt-parts-list');
    if (!list) return;
    var modeLabel = state.partsMode === 'advanced' ? 'Advanced' : 'Simple';
    // Settings is the LAST card in the strip, styled like a part.
    var settingsCard = '<div class="vt-parts-row vt-parts-row--settings" data-parts-settings role="button" tabindex="0">'
      + '<div class="vt-parts-thumb-wrap vt-parts-settings-icon"><span>⚙</span></div>'
      + '<div class="vt-parts-info">'
      +   '<span class="vt-parts-name">Settings</span>'
      +   '<span class="vt-parts-sub" id="vt-parts-mode-label">' + modeLabel + '</span>'
      + '</div>'
      + '</div>';
    // Lining is the last PART before Settings; tapping it opens the lining
    // sub-panel rather than the colour swatches (lining colour is fixed).
    var lin = state.selectedLining;
    var liningCard = '<div class="vt-parts-row vt-parts-row--lining" data-lining-row role="button" tabindex="0">'
      + '<div class="vt-parts-thumb-wrap">'
      +   '<div class="vt-parts-lining-swatch vt-lining-art vt-lining-art--' + lin.id + '"></div>'
      + '</div>'
      + '<div class="vt-parts-info">'
      +   '<span class="vt-parts-name">Lining</span>'
      +   '<span class="vt-parts-sub">' + lin.name.replace(/\s*\(recommended\)/i, '') + '</span>'
      + '</div>'
      + '</div>';
    var rows = liveGroups.map(function (g) {
      var color = g.lastColor ? g.lastColor.color : '#D4D0C8';
      var active = g.id === activePartId ? ' vt-parts-row--active' : '';
      return '<div class="vt-parts-row' + active + '" data-group-id="' + g.id + '" role="button" tabindex="0">'
           + '<div class="vt-parts-thumb-wrap">'
           +   '<img class="vt-parts-thumb" src="' + PLACEHOLDER_IMG + '" data-thumb-for-part="' + g.id + '" alt="' + displayLabel(g.label) + '">'
           + '</div>'
           + '<div class="vt-parts-info">'
           +   '<div class="vt-parts-foot">'
           +     '<span class="vt-parts-dot" data-dot-for-part="' + g.id + '" style="background:' + color + '"></span>'
           +     '<span class="vt-parts-name">' + displayLabel(g.label) + '</span>'
           +   '</div>'
           +   (isWeltGroup(g) && shoeHasWelt() ? weltPillsHtml() : materialPillsHtml(g))
           + '</div>'
           + '</div>';
    });
    // Welt off: its stand-in card goes where the welt row sat (before
    // stitching, after laces), so turning it back on doesn't shuffle the strip.
    if (shoeHasWelt() && !weltOn()) {
      var at = liveGroups.findIndex(function (g) { return g.isStitch; });
      rows.splice(at === -1 ? rows.length : at, 0, weltOffRowHtml());
    }
    list.innerHTML = rows.join('') + liningCard + settingsCard;
    setupScrollFade(list); // strip content changed — recompute the edge fade
  }

  // One-line subtitle naming the colour currently on a part. Used as the second
  // line for parts that have no leather/suede pills (laces, stitching, sole, welt)
  // so their cards stand as tall as the pill cards instead of looking clipped.
  function partColorSubHtml(group) {
    var hex  = group.lastColor && group.lastColor.color;
    var name = hex ? ((group.lastColor.label || D.hexLabel[hex] || D.hexLabel[hex.toUpperCase()] || hex)) : '';
    return '<span class="vt-parts-sub vt-parts-sub--color">' + name + '</span>';
  }

  // Leather/Suede pill toggle. Skipped for laces/stitching/sole/welt (those
  // use their own materials, not a leather/suede swap) and skipped when no
  // reference textures were built — in both cases the colour-name subtitle stands
  // in so the card keeps its height. Otherwise renders both pills; the one
  // matching the detected current kind is shown active, or neither if detection
  // didn't tag this group's material.
  function materialPillsHtml(group) {
    if (group.isLaces || group.isStitch || group.isSole) return partColorSubHtml(group);
    var refs = preload.referenceTextures || {};
    var hasAny = D.materialKinds.some(function (mk) { return !!refs[mk.id]; });
    if (!hasAny) return partColorSubHtml(group);
    var kind = P.getGroupMaterialKind(group);
    return '<div class="vt-parts-mat" data-mat-for-part="' + group.id + '">'
         + D.materialKinds.map(function (mk) {
             var disabled = refs[mk.id] ? '' : ' disabled';
             var sel = kind === mk.id ? ' vt-parts-mat-btn--active' : '';
             return '<button type="button" class="vt-parts-mat-btn' + sel + '"'
                  + ' data-material-btn data-group-id="' + group.id + '"'
                  + ' data-kind="' + mk.id + '"' + disabled + '>'
                  +   mk.label
                  + '</button>';
           }).join('')
         + '</div>';
  }

  function applyPartMaterial(groupId, kind) {
    var g = liveGroups.find(function (gg) { return gg.id === groupId; });
    if (!g) return;
    var src = kindSwapSource(kind);
    if (!src) return;

    // Keeps the part's current colour as an override, swaps, records the kind.
    K.swapPartMaterial(g, kind, src, state.partColorsByMeshName, state.partMaterialsByMeshName);
    state.partMaterials[g.id] = kind;

    // Update the toggle's visual state for this row.
    var matWrap = document.querySelector('[data-mat-for-part="' + g.id + '"]');
    if (matWrap) {
      matWrap.querySelectorAll('[data-material-btn]').forEach(function (b) {
        b.classList.toggle('vt-parts-mat-btn--active', b.dataset.kind === kind);
      });
    }

    // Recapture the part thumbnail — material change can shift the silhouette
    // background subtly (the part itself is rendered with a flat colour, so
    // the thumb is largely unchanged, but kept for parity with paintActivePart).
    var img = document.querySelector('[data-thumb-for-part="' + g.id + '"]');
    if (img) {
      var url = viewer.capturePartThumbnail(g);
      if (url) img.src = url;
    }
    // Rebuild the mirrored LEFT shoe so the swapped material shows on both feet —
    // the clone captures materials as they stand, so a stale partner keeps the old one.
    viewer.reflowFloor();
    updateBackdropFromShoe();
  }

  // Generate one part-thumbnail per animation frame so the UI stays responsive.
  var partsThumbJob = null;
  var partsThumbToken = 0;
  function refreshPartsThumbs() {
    if (!viewer || !liveGroups.length) return;
    var token = ++partsThumbToken;
    partsThumbJob = { i: 0, token: token };
    requestAnimationFrame(stepPartsThumb);
  }
  function stepPartsThumb() {
    if (!partsThumbJob || partsThumbJob.token !== partsThumbToken) return;
    if (partsThumbJob.i >= liveGroups.length) { partsThumbJob = null; return; }
    var group = liveGroups[partsThumbJob.i++];
    try {
      var url = viewer.capturePartThumbnail(group);
      if (url) {
        var sel = '[data-thumb-for-part="' + group.id + '"]';
        document.querySelectorAll(sel).forEach(function (img) { img.src = url; });
      }
    } catch (e) {
      // One bad part shouldn't stop the rest of the strip from filling in.
      console.error('[vitrine] part thumbnail failed:', e);
    }
    requestAnimationFrame(stepPartsThumb);
  }

  function selectPart(groupId, opts) {
    opts = opts || {};
    activePartId = groupId;
    var panel = stepPanel('parts');
    if (!panel) return;
    panel.querySelectorAll('.vt-parts-row').forEach(function (r) {
      r.classList.toggle('vt-parts-row--active', r.dataset.groupId === groupId);
    });
    var sw = document.getElementById('vt-parts-swatches-wrap');
    if (sw) sw.hidden = false;
    var group = liveGroups.find(function (g) { return g.id === groupId; });
    if (opts.scrollIntoView) {
      var row = panel.querySelector('.vt-parts-row[data-group-id="' + groupId + '"]');
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    // Brief 3D flash so the user sees which part they picked. We deliberately do
    // NOT swing the camera to the part here — auto-reorbiting on every tap made
    // the shoe feel like it was spinning on its own and fought the user's manual
    // rotation (worst on mobile). The shoe stays put; the user rotates it by hand.
    if (group && viewer.flashPart) viewer.flashPart(group);
  }

  function renderSwatches() {
    var sw = document.getElementById('vt-parts-swatches');
    if (!sw) return;
    sw.innerHTML = D.swatchPalette.map(function (p, i) {
      var border = p.color === '#FFFFFF' ? 'rgba(0,0,0,0.18)' : 'transparent';
      return '<button class="vt-swatch" data-swatch-idx="' + i + '" style="background:' + p.color + ';border-color:' + border + '" title="' + p.label + '"></button>';
    }).join('');
  }

  function paintActivePart(swatch) {
    var g = liveGroups.find(function (gg) { return gg.id === activePartId; });
    if (!g) return;
    state.partColors[g.id] = swatch;
    g.meshes.forEach(function (m) { if (m.name) state.partColorsByMeshName[m.name] = swatch; });
    P.paintMeshes(g.meshes, swatch.color);
    g.lastColor = swatch;
    if (g.isSole) {
      var sides = viewer.getSoleSideMeshes();
      if (sides.length) P.paintMeshes(sides, swatch.color);
    }
    var dot = document.querySelector('[data-dot-for-part="' + g.id + '"]');
    if (dot) dot.style.background = swatch.color;
    // Recapture the thumbnail for this part with its new colour.
    var img = document.querySelector('[data-thumb-for-part="' + g.id + '"]');
    if (img) {
      var url = viewer.capturePartThumbnail(g);
      if (url) img.src = url;
    }
    // Rebuild the mirrored LEFT shoe so the part's new colour shows on both feet —
    // the clone captures materials as they stand, so a stale partner keeps the old one.
    viewer.reflowFloor();
    updateBackdropFromShoe();
  }

  function switchPartsMode(mode) {
    state.partsMode = mode;
    repaintReview();
    renderPartsStrip();
    refreshPartsThumbs();
  }

  function resetPartOverrides() {
    state.partColors = {};
    state.partColorsByMeshName = {};
    state.partMaterials = {};
    state.partMaterialsByMeshName = {};
    state.materialMode = 'mixed';
    // Material swaps mutate live materials (m.map + userData.cfKind on the
    // cloned material instances). Clearing state isn't enough — we need a
    // fresh GLB load to drop the swap textures and restore the original kinds.
    // Otherwise a previously-swapped mesh would still show as Suede after reset.
    loadedShoeId = null;
    ensureLiveScene();
    renderPartsStrip();
    refreshPartsThumbs();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CART — add line item, naming modal, login prompt
  // ──────────────────────────────────────────────────────────────────────────
  function openNotesModal() {
    var modal = document.getElementById('vt-notes-modal');
    var input = document.getElementById('vt-notes-input');
    input.value = state.notes || '';
    modal.classList.remove('vt-hidden');
    setTimeout(function () { input.focus(); }, 50);
  }

  // opts.btn      — the button driving the add (defaults to the review's Add to cart)
  // opts.thenScan — true when triggered from the "save before photos" prompt: on
  //                 success the button reads "Done" and we head to the scan page
  //                 instead of opening the naming modal.
  function addToCart(opts) {
    opts = opts || {};
    var btn = opts.btn || document.getElementById('vt-add-to-cart');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding to Cart'; }

    var customization = serializeCustomization();
    var properties = humanProperties();
    // Frozen resolved build spec — authoritative for fulfilment.
    properties['_spec'] = JSON.stringify(buildSpec());
    // Recipe — for re-opening/editing the build only, NOT for fulfilment.
    properties['_customization'] = JSON.stringify(customization);
    properties['_edit_url'] = buildShareUrl(customization);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: D.cartVariantId, quantity: 1, properties: properties })
    })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
      .then(function () {
        state.addedToCart = true; // so the Take photos anchor skips the save prompt
        if (opts.onDone) { if (btn) btn.textContent = 'Saved'; opts.onDone(); return; }
        if (opts.thenScan) {
          if (btn) btn.textContent = 'Done';
          setTimeout(function () { location.href = '/pages/scan'; }, 700);
        } else {
          openNamingModal();
        }
      })
      .catch(function (err) {
        console.error('[vitrine] cart failed:', err);
        if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
        alert('Could not add to cart. Please try again.');
      });
  }

  // Update the existing cart line (reopened via a cart "Edit" link) in place,
  // instead of adding a new one. Keeps the build's existing name.
  function updateCartLine(opts) {
    opts = opts || {};
    if (!state.editingLineKey) return addToCart(opts);
    var btn = opts.btn || document.getElementById('vt-edit-in-cart');
    var btnLabel = btn ? btn.textContent : 'Edit shoe in cart';
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

    var customization = serializeCustomization();
    var properties = humanProperties();
    if (state.name) properties['Name'] = state.name;
    properties['_spec'] = JSON.stringify(buildSpec());
    properties['_customization'] = JSON.stringify(customization);
    properties['_edit_url'] = buildShareUrl(customization);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: state.editingLineKey, properties: properties })
    })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
      .then(function () { if (opts.onDone) { opts.onDone(); return; } location.href = '/cart'; })
      .catch(function (err) {
        console.error('[vitrine] cart update failed:', err);
        if (btn) { btn.disabled = false; btn.textContent = btnLabel; }
        alert('Could not update the cart. Please try again.');
      });
  }

  // Bump when the spec shape changes, so fulfilment can tell which resolver/
  // format produced a saved order.
  var BUILD_SPEC_VERSION = 1;

  // Frozen, fully-resolved snapshot of the shoe as configured — the AUTHORITATIVE
  // build spec for the workshop. Computed once from the painted live scene at
  // purchase and never re-resolved (re-running the resolver later, after a
  // colorway/palette/detection change, could yield a different shoe than the
  // customer bought). Two views of the SAME resolved scene:
  //   • zones — grouped at the simple (material × colour) level, the readable
  //     summary the customer saw.
  //   • parts — EVERY anatomical part listed by name with its own material +
  //     colour, so no per-part advanced edit is ever lost to grouping.
  // The colorway + diffs in serializeCustomization() are kept only for
  // re-opening/editing the build, NOT for fulfilment.
  function buildSpec() {
    var pos = D.soleStackPositions[state.selectedSoleIdx];
    var scene = viewer && viewer.getModelScene();
    var spec = {
      v:        BUILD_SPEC_VERSION,
      ts:       new Date().toISOString(),
      style:    state.selectedShoe.name,
      styleId:  state.selectedShoe.id,
      zones:    groupedZones(),
      parts:    perPartZones(scene),
      lining:   state.selectedLining.name,
      // Stack: readable label + discrete fields so the tech sheet can read the
      // construction without parsing the label string.
      sole:     pos.label + ' · ' + pos.totalMm + 'mm',
      soleId:   pos.id,
      soleType: pos.sole,                                              // ground | cloud
      midsole:  (pos.midsole && pos.midsole !== 'none') ? cap(pos.midsole) : 'None',
      footbed:  pos.footbed ? '1× Cork, 1× Foam' : 'None',
      stackMm:  pos.totalMm,
      welt:     weltOn()
    };
    // The rubber the sole edge was shown in (vitrine-core 0.3.2). Its presence
    // marks the order as placed with rubber soles; getscans keys off it.
    if (D.soleRubber && D.soleRubberHex && viewer && viewer.getSoleSideMeshes) {
      var edge = viewer.getSoleSideMeshes()[0];
      var edgeHex = edge && edge.material && edge.material.userData && edge.material.userData.cfHex;
      if (edgeHex) spec.soleRubber = edgeHex.toUpperCase() === D.soleRubberHex.Brown.toUpperCase() ? 'Brown' : 'Black';
    }
    if (state.notes) spec.notes = state.notes;
    if (state.name)  spec.name  = state.name;
    return spec;
  }

  // Grouped (material × colour) view — the human-readable summary.
  function groupedZones() {
    var rows = (liveGroups && liveGroups.length) ? regroupSimpleBodies(liveGroups) : [];
    var zones = [];
    rows.forEach(function (g) {
      var hex = (g.lastColor && g.lastColor.color) || groupHex(g.meshes);
      if (!hex) return;
      var kind = P.getGroupMaterialKind(g);
      zones.push({
        zone:     displayLabel(g.label),
        material: kind ? cap(kind) : null,
        color:    (g.lastColor && g.lastColor.label) || D.hexLabel[hex] || hex,
        hex:      hex
      });
    });
    return zones;
  }

  // Per-part view — every anatomical part by name, resolved straight from the
  // painted scene. Captures every advanced per-part edit even when two parts
  // share a material+colour (which the grouped view would merge into one row).
  function perPartZones(scene) {
    if (!scene) return [];
    var parts = [];
    dropHiddenMeshes(P.groupByPrefix(scene)).forEach(function (g) {
      var hex = groupHex(g.meshes);
      if (!hex) return;
      var kind = P.getGroupMaterialKind(g);
      parts.push({
        part:     displayLabel(g.label),
        material: kind ? cap(kind) : null,
        color:    D.hexLabel[hex] || hex,
        hex:      hex
      });
    });
    return parts;
  }

  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function safeParse(s) { try { return JSON.parse(s || '{}'); } catch (e) { return {}; } }

  // First resolved colour hex among a group's meshes.
  function groupHex(meshes) {
    if (!meshes) return '';
    for (var i = 0; i < meshes.length; i++) {
      var mats = Array.isArray(meshes[i].material) ? meshes[i].material : [meshes[i].material];
      var m = mats[0];
      if (m && m.color) return '#' + m.color.getHexString().toUpperCase();
    }
    return '';
  }

  // Human-readable cart line-item properties (the printed tech sheet). Uses the
  // grouped view; the structured _spec carries the full per-part breakdown.
  function humanProperties() {
    var spec = buildSpec();
    var props = { 'Style': spec.style };
    props['Materials'] = materialsSummary();
    spec.zones.forEach(function (z) { props[z.zone] = z.color; });
    props['Lining']  = spec.lining;
    props['Stack']   = spec.sole;   // renamed from 'Sole' so it can't clobber the "Sole" colour zone
    // 'Sole' = the sole this build ships on (Vibram rubber 4mm / NewFlex 8mm;
    // NewFlex 4mm + Cork on legacy builds). Deliberately overwrites the "Sole" colour zone the
    // loop above may have written: the unit is what fulfilment needs, and the
    // painted sole colour is still in _spec.zones.
    props['Sole']    = soleComboName(D.soleStackPositions[state.selectedSoleIdx]);
    props['Midsole'] = spec.midsole;
    props['Footbed'] = spec.footbed;
    // With a welt its colour is already a zone ("Welt"); say so when there's none.
    if (shoeHasWelt() && !spec.welt) props['Welt'] = 'None';
    if (state.notes) props['Notes'] = state.notes;
    return props;
  }

  // Compact, URL-safe base64 of the customization. Short keys + omitted defaults
  // keep the link readable. encodeShare returns just the `?cfg=` token; buildShareUrl
  // wraps it into the absolute URL used by the Share button and the cart edit link.
  function encodeShare(customization) {
    var raw = btoa(unescape(encodeURIComponent(JSON.stringify(customization))));
    return raw.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
  }
  function buildShareUrl(customization) {
    return location.origin + location.pathname + '?cfg=' + encodeShare(customization);
  }

  // The build is intentionally NOT mirrored into the address bar as you edit —
  // the URL stays clean while building. `?cfg=` only ever arrives from outside (a
  // Share link or the cart "Edit your design" link) and is read once on load by
  // tryRestore. The Share button below produces that link on demand.
  function shareLink(btnEl) {
    var btn = btnEl || document.getElementById('vt-share');
    if (!btn) return;
    var url = buildShareUrl(serializeCustomization());
    var message = "I built this shoe in the Vitrine on cobbler.fit — here's the link";
    var original = btn.dataset.original || btn.textContent;
    btn.dataset.original = original;

    function flash(msg) {
      btn.textContent = msg;
      setTimeout(function () { btn.textContent = btn.dataset.original; }, 1800);
    }

    // Capture synchronously so clipboard.write() stays inside the click gesture.
    var imageDataUrl = captureShareDataUrl();
    var fileName = (state.name || state.selectedShoe.id || 'shoe').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase() + '.png';
    var file = imageDataUrl ? dataUrlToFile(imageDataUrl, fileName) : null;

    // Mobile native share — preferred when it can attach files.
    if (navigator.share && file && navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({ text: message, url: url, files: [file] })
        .then(function () { flash('Shared'); })
        .catch(function (err) { if (err && err.name !== 'AbortError') copyRich(message, url, imageDataUrl, flash); });
      return;
    }

    // Desktop: rich clipboard. Outlook / Gmail / Word will paste the image + text.
    copyRich(message, url, imageDataUrl, flash);
  }

  function captureShareDataUrl() {
    var canvas = document.getElementById('vt-canvas');
    if (!canvas || !canvas.toDataURL) return null;
    try { return canvas.toDataURL('image/png'); } catch (e) { return null; }
  }

  function dataUrlToFile(dataUrl, name) {
    try {
      var parts = dataUrl.split(',');
      var mime  = parts[0].match(/:(.*?);/)[1];
      var bin   = atob(parts[1]);
      var arr   = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new File([arr], name, { type: mime });
    } catch (e) { return null; }
  }

  // Rich clipboard: writes text/html (with the image as a data URL) plus
  // a text/plain fallback in one call. Apps that paste HTML (Outlook, Gmail,
  // Word, Notion) get the picture inline; pure-text targets get the message + link.
  function copyRich(message, url, imageDataUrl, onDone) {
    var plain = message + ' ' + url;
    if (imageDataUrl && w.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
      var html = '<p>' + escapeHtml(message) + '<br>'
               +   '<a href="' + escapeHtml(url) + '">' + escapeHtml(url) + '</a>'
               + '</p>'
               + '<p><img src="' + escapeHtml(imageDataUrl) + '" alt="My shoe" style="max-width:480px;height:auto"></p>';
      try {
        var item = new ClipboardItem({
          'text/html':  new Blob([html],  { type: 'text/html'  }),
          'text/plain': new Blob([plain], { type: 'text/plain' })
        });
        navigator.clipboard.write([item])
          .then(function () { onDone('Copied'); })
          .catch(function () { copyToClipboard(plain, onDone); });
        return;
      } catch (e) { /* fall through */ }
    }
    copyToClipboard(plain, onDone);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function copyToClipboard(text, onDone) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(function () { onDone('Link copied'); })
        .catch(function () { legacyCopy(text, onDone); });
      return;
    }
    legacyCopy(text, onDone);
  }

  function legacyCopy(text, onDone) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
    onDone(ok ? 'Link copied' : 'Copy failed');
  }

  // Short keys + omitted defaults so the encoded URL stays compact.
  // Decoded in tryRestore (which also accepts the legacy long-key form).
  //
  // The painter-group "bucket" a mesh belongs to: its anatomical prefix, or a
  // shared special bucket for laces / stitch / sole / welt. Edits are uniform
  // within a bucket (a part's layer meshes always share one colour/material), so
  // the bucket — not the individual mesh — is the unit of customisation. Used by
  // BOTH serialisation (one representative per bucket → short URLs) and
  // repaintReview (resolves every mesh's override from its bucket → lossless).
  function colorBucket(name) { return K.colorBucket(name); }

  // bucket → swatch / kind, built from the per-mesh override maps. repaintReview
  // resolves each mesh by its bucket, so a single saved representative per bucket
  // restores the whole part.
  function bucketColorMap() { return K.bucketMap(state.partColorsByMeshName); }
  function bucketMaterialMap() { return K.bucketMap(state.partMaterialsByMeshName); }

  // Color/material overrides are stored as {hex: [buckets]} / {kind: [buckets]}.
  // We save the bucket KEY (anatomical prefix or a __laces__/__stitch__/__sole__/
  // __welt__ token) — never a full mesh name. The bucket is the real unit of
  // customisation and is far shorter, so share URLs stay compact; repaintReview
  // re-expands each bucket to all of its layer meshes on restore.
  function serializeCustomization() {
    var data = { m: state.selectedShoe.id, c: state.selectedColorway.id };
    if (state.selectedLining.id !== 'leather') data.l  = state.selectedLining.id;
    if (state.selectedSoleIdx !== 1)           data.s  = state.selectedSoleIdx;
    if (state.partsMode !== 'simple')          data.p  = state.partsMode;
    if (!state.welt)                           data.w  = 0;

    var ov = K.serializeOverrides(state.partColorsByMeshName, state.partMaterialsByMeshName);
    if (ov.x)  data.x  = ov.x;
    if (ov.mt) data.mt = ov.mt;
    if (state.notes) data.n  = state.notes;
    if (state.name)  data.nm = state.name;
    return data;
  }

  function openNamingModal() {
    var modal = document.getElementById('vt-name-modal');
    var save  = document.getElementById('vt-name-save');
    var skip  = document.getElementById('vt-name-skip');
    var scan  = document.getElementById('vt-name-scan');
    var btn   = document.getElementById('vt-add-to-cart');

    // The cart row has already been re-rendered to the "added" state, so the
    // original Add to cart button may be gone — reset it only if it's still there.
    if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
    if (beta) beta.onAdded();
    if (scan) scan.textContent = (w.cfHasScan && w.cfHasScan()) ? 'View measurements' : 'Take my photos';
    modal.classList.remove('vt-hidden');

    function closeModal() {
      modal.classList.add('vt-hidden');
    }

    save.onclick = function () {
      closeModal();
      goToStep(BETA ? 'build' : 'design');
    };

    skip.onclick = function () {
      closeModal();
      location.href = '/cart';
    };
  }

  function tryRestore(token) {
    try {
      // URL-safe base64 → standard, repad.
      var safe = token.replace(/-/g, '+').replace(/_/g, '/');
      while (safe.length % 4) safe += '=';
      var data = JSON.parse(decodeURIComponent(escape(atob(safe))));

      // Accept both compact keys (new) and long keys (legacy carts).
      var modelId    = data.m  || data.model;
      var colorwayId = data.c  || data.colorway;
      var liningId   = data.l  || data.lining;
      var soleIdx    = (typeof data.s === 'number') ? data.s : data.soleIdx;
      var partsMode  = data.p  || data.partsMode;
      var colors     = data.x  || data.colors;
      var materials  = data.mt || data.materials;
      var notes      = data.n  || data.notes;
      var nm         = data.nm || data.name;

      var shoe = D.shoes.find(function (s) { return s.id === modelId; });
      var cw   = D.colorways.find(function (c) { return c.id === colorwayId; });
      var lin  = D.liningOptions.find(function (l) { return l.id === liningId; });
      // Body-colour overrides from the product page's Advanced colour mode:
      // b1 = leather body hex, b2 = suede body hex, layered on top of the base
      // colorway `c` (which still supplies sole / laces / stitch). Cloned so
      // the shared D.colorways entry is never mutated; the synthetic id just
      // means no card shows as active on the colors step, which is honest —
      // this build isn't one of the curated colorways any more.
      if (cw && (data.b1 || data.b2)) {
        var mixed = {};
        for (var mk in cw) if (Object.prototype.hasOwnProperty.call(cw, mk)) mixed[mk] = cw[mk];
        mixed.id = cw.id + '-mix';
        if (data.b1) mixed.body1 = { color: data.b1, label: D.labelForHex(data.b1) };
        if (data.b2) mixed.body2 = { color: data.b2, label: D.labelForHex(data.b2) };
        mixed.label = (mixed.body1 ? mixed.body1.label : '?') + ' & ' + (mixed.body2 ? mixed.body2.label : '?');
        cw = mixed;
      }
      // "No lining" was retired — coerce any old share back to the default lining.
      if (lin && lin.id === 'none') lin = liningChoices()[0];
      if (shoe) state.selectedShoe = shoe;
      // Keep the design-entry baseline in sync so the jump to review (which leaves
      // the design step) doesn't see a "shoe changed" and wipe the restored edits.
      designEntryShoeId = state.selectedShoe.id;
      if (cw)   state.selectedColorway = cw;
      if (lin)  state.selectedLining = lin;
      // 0, 1 and 3 are selectable; 2 (the retired cork midsole) lands on Cloud,
      // its nearest feel. Out-of-range values fall back to ground + footbeds.
      if (typeof soleIdx === 'number') state.selectedSoleIdx = soleIdx === 2 ? 3 : ((soleIdx >= 0 && soleIdx <= 3) ? soleIdx : 1);
      state.partsMode = partsMode || 'simple';
      state.welt = data.w !== 0;
      // Accept the inverted form ({hex: [buckets]}) plus the legacy flat form
      // ({name: hex}). Array entries are bucket keys today (older links stored a
      // representative mesh name) — either resolves correctly because bucketColorMap
      // re-buckets every key through colorBucket() before repaint.
      var ovs = K.parseOverrides(colors, materials);
      Object.keys(ovs.colors).forEach(function (n) { state.partColorsByMeshName[n] = ovs.colors[n]; });
      Object.keys(ovs.mats).forEach(function (n) { state.partMaterialsByMeshName[n] = ovs.mats[n]; });
      state.notes = notes || '';
      state.name  = nm    || '';
      // Remove the restore token from the URL once the shoe is loaded.
      if (window.history && window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.delete('cfg'); u.searchParams.delete('r'); u.searchParams.delete('restore');
        window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
      }
      // Skip straight to review on restore.
      setTimeout(function () { goToStep(BETA ? 'build' : 'review'); }, 100);
    } catch (e) {
      console.warn('[vitrine] restore failed:', e);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // BOOTH MODE (?viewas=booth) — kiosk demo for events / the shop window.
  // Fullscreen live viewer, continuous turntable, and every BOOTH_INTERVAL_MS a
  // new random build: shoe, colourway (from that shoe's curated rotation pool),
  // lining and sole stack. "Tap to start" pauses it, asks for an email
  // (snippets/cf-soft-signin.liquid), then drops the visitor into the normal
  // Vitrine on the Design step with the shoe they were looking at centred.
  // Ported from the pre-Vitrine shoebuilder's booth branch.
  // ──────────────────────────────────────────────────────────────────────────
  var BOOTH_INTERVAL_MS = 4000;
  var BOOTH_ROTATE_SPEED = 1.6;     // OrbitControls autoRotateSpeed (2.0 ≈ 30 s / turn)
  var boothActive = false;
  var boothTimer = null;
  var boothLast = { shoeId: null, cwId: null };

  function boothPick(arr, avoid) {
    var pool = arr.filter(function (x) { return x !== avoid; });
    if (!pool.length) pool = arr;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function boothShoes() {
    return D.shoes.filter(function (s) { return !!s.glb && !s.comingSoon; });
  }

  // Pick the next build. Prefers a different shoe each time, and never repeats
  // the colourway just shown.
  function boothNextBuild() {
    var shoes = boothShoes();
    var shoe = boothPick(shoes, shoes.filter(function (s) { return s.id === boothLast.shoeId; })[0]);
    var pool = rotationPoolForShoe(shoe.id);
    var cw = boothPick(pool, pool.filter(function (c) { return c.id === boothLast.cwId; })[0]);
    var linings = liningChoices();
    var lining = linings[Math.floor(Math.random() * linings.length)] || state.selectedLining;
    // The two shop picks only: Ground-Feel with footbeds (1) / Cloud-Feel (3).
    // idx 0 (bare ground) and 2 (retired cork) never appear in the demo.
    var soleIdx = Math.random() < 0.5 ? 1 : 3;
    boothLast = { shoeId: shoe.id, cwId: cw && cw.id };
    return { shoe: shoe, colorway: cw, lining: lining, soleIdx: soleIdx };
  }

  function boothApply(build) {
    state.selectedShoe     = build.shoe;
    if (build.colorway) state.selectedColorway = build.colorway;
    state.selectedLining   = build.lining;
    state.selectedSoleIdx  = build.soleIdx;
    state.partColors = {}; state.partColorsByMeshName = {};
    state.partMaterials = {}; state.partMaterialsByMeshName = {};
    ensureLiveScene();
  }

  function boothTick() {
    if (!boothActive || !viewer) return;
    // A shoe GLB is still loading/decoding — skip this beat rather than stack loads.
    if (!viewer.getModelScene()) return;
    boothApply(boothNextBuild());
  }

  function startBooth() {
    if (!viewer) return;
    boothActive = true;
    state.skipWelcome = true;
    document.body.classList.add('vt-booth-mode');
    goToStep('review', { instant: true, noPush: true });
    if (viewer.setAutoRotate) viewer.setAutoRotate(true, BOOTH_ROTATE_SPEED);
    boothApply(boothNextBuild());
    boothTimer = setInterval(boothTick, BOOTH_INTERVAL_MS);

    var tap = document.getElementById('vt-booth-tap');
    if (tap) {
      tap.hidden = false;
      tap.addEventListener('click', onBoothTap);
    }
  }

  function onBoothTap() {
    if (!boothActive) return;
    // Freeze on the current build while the email popup is up.
    if (boothTimer) { clearInterval(boothTimer); boothTimer = null; }
    document.body.classList.add('vt-booth-signing');
    if (typeof window.cfShowSignin === 'function') {
      window.cfShowSignin(function () { exitBooth(); }, { force: true });
    } else {
      exitBooth();
    }
  }

  function exitBooth() {
    boothActive = false;
    if (boothTimer) { clearInterval(boothTimer); boothTimer = null; }
    if (viewer && viewer.setAutoRotate) viewer.setAutoRotate(false);
    var tap = document.getElementById('vt-booth-tap');
    if (tap) { tap.hidden = true; tap.removeEventListener('click', onBoothTap); }
    document.body.classList.remove('vt-booth-mode', 'vt-booth-signing');
    // Drop ?viewas=booth so a refresh lands in the normal Vitrine.
    if (window.history && window.history.replaceState) {
      var u = new URL(window.location.href);
      u.searchParams.delete('viewas');
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    }
    // Fresh start on Design with the shoe they were watching centred. The booth's
    // colourway / lining / sole stay as the starting picks.
    state.reachedReview = false;
    goToStep('design', { instant: true });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // GLOBAL HANDLERS — back/next, modal closes, notes save, parts cog
  // ──────────────────────────────────────────────────────────────────────────
  function bindGlobalHandlers() {
    // Refresh, tab close, or any navigation that unloads the page: the browser's
    // native "stay on this page / leave" prompt, but only once there's a design
    // worth losing. (In-Vitrine back/forward is handled by setupHistoryNav.)
    window.addEventListener('beforeunload', function (e) {
      if (!hasProgress()) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    });

    // Step nav: any .vt-btn--next / .vt-btn--back inside a step
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-nav="next"]')) { nextStep(); return; }
      if (e.target.closest('[data-nav="back"]')) { prevStep(); return; }
      if (e.target.closest('[data-help]'))       { openShopifyChat(); return; }
    });

    // Notes modal save
    var notesModal = document.getElementById('vt-notes-modal');
    if (notesModal) {
      notesModal.addEventListener('click', function (e) {
        if (e.target.closest('[data-notes-save]')) {
          state.notes = document.getElementById('vt-notes-input').value.trim();
          notesModal.classList.add('vt-hidden');
        }
        if (e.target === notesModal || e.target.closest('[data-notes-cancel]')) {
          notesModal.classList.add('vt-hidden');
        }
      });
    }

    // Footbed warning modal close
    var warnModal = document.getElementById('vt-footbed-warn');
    if (warnModal) {
      warnModal.addEventListener('click', function (e) {
        if (e.target === warnModal || e.target.closest('[data-warn-close]')) closeFootbedWarning();
      });
    }

    // "Save before photos" prompt. Add to cart → "Adding to Cart" → "Done" → scan.
    // "Not now" skips saving but still heads to the scan page; × just cancels.
    var scanSaveModal = document.getElementById('vt-scan-save-modal');
    if (scanSaveModal) {
      scanSaveModal.addEventListener('click', function (e) {
        if (e.target.closest('#vt-scan-save-add')) {
          addToCart({ btn: document.getElementById('vt-scan-save-add'), thenScan: true });
          return;
        }
        if (e.target.closest('[data-scan-save-skip]')) {
          scanSaveModal.classList.add('vt-hidden');
          location.href = '/pages/scan';
          return;
        }
        if (e.target === scanSaveModal || e.target.closest('[data-scan-save-close]')) {
          scanSaveModal.classList.add('vt-hidden');
        }
      });
    }

    // Login prompt
    var loginModal = document.getElementById('vt-login-modal');
    if (loginModal) {
      loginModal.addEventListener('click', function (e) {
        if (e.target.closest('[data-login-go]')) {
          // The address bar is no longer kept in sync with the build, so rebuild
          // the restore link here — signing in then returns to this exact shoe.
          var ret = location.pathname + '?cfg=' + encodeShare(serializeCustomization());
          if (state.editingLineKey) ret += '&line=' + encodeURIComponent(state.editingLineKey);
          if (BETA) ret += '&beta=true';
          location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(ret);
        }
        if (e.target.closest('[data-login-skip]')) {
          location.href = '/cart';
        }
      });
    }

    // Review "Size" (i): click toggles a small tip bubble; clicking elsewhere
    // (or the (i) again) closes it. The summary is re-rendered, so this is a
    // delegated document handler rather than bound to the button itself.
    function closeSizeTips(except) {
      document.querySelectorAll('.vt-size-info-wrap.vt-tip-open').forEach(function (w) {
        if (w === except) return;
        w.classList.remove('vt-tip-open');
        var b = w.querySelector('[data-size-info]');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
    // The tip is position:fixed; place it centred over the (i) but clamped to the
    // viewport so it can't run off-screen on mobile. Prefer above the (i); flip
    // below when there isn't room.
    function positionSizeTip(btn, tip) {
      var margin = 8;
      var r  = btn.getBoundingClientRect();
      var tr = tip.getBoundingClientRect(); // measured while open (display:block)
      var left = r.left + r.width / 2 - tr.width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - tr.width - margin));
      var top = r.top - tr.height - margin;
      if (top < margin) top = r.bottom + margin;
      tip.style.left = left + 'px';
      tip.style.top  = top + 'px';
      // A transformed ancestor (the sliding .vt-step, a hovered card) becomes
      // the containing block for position:fixed — measure where it actually
      // landed and take the offset back out.
      var got = tip.getBoundingClientRect();
      tip.style.left = (left - (got.left - left)) + 'px';
      tip.style.top  = (top  - (got.top  - top))  + 'px';
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-size-info]');
      if (!btn) { closeSizeTips(null); return; }
      var wrap = btn.closest('.vt-size-info-wrap');
      var open = wrap && wrap.classList.toggle('vt-tip-open');
      btn.setAttribute('aria-expanded', String(!!open));
      closeSizeTips(wrap); // collapse any other open tip
      if (open) { var tip = wrap.querySelector('.vt-size-tip'); if (tip) positionSizeTip(btn, tip); }
    });
    // Fixed-positioned tip would drift on scroll — just close it.
    window.addEventListener('scroll', function () { closeSizeTips(null); }, true);
    window.addEventListener('resize', function () { closeSizeTips(null); });

    // ESC closes any open modal
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeSizeTips(null);
      ['vt-notes-modal', 'vt-name-modal', 'vt-login-modal', 'vt-footbed-warn', 'vt-scan-save-modal'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el && !el.classList.contains('vt-hidden')) el.classList.add('vt-hidden');
      });
    });

    // Arrow keys scroll the Design carousel (← previous shoe, → next shoe), the
    // same as the on-screen arrows / swipe. Ignored while typing in a field.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (state.currentStep !== 'design' || !carousel) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') carousel.prev(); else carousel.next();
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // BETA (?beta=true) — the one-page Vitrine. The UI lives in
  // assets/cf-vitrine-beta.js (window.VitrineBeta); this is only the shell (the
  // extra step panel) and the narrow API it drives the build through, so every
  // change still goes through the same state, painter and cart code.
  // ──────────────────────────────────────────────────────────────────────────
  function setupBetaShell() {
    document.body.classList.add('vt-beta');
    if (D.stepsWithCanvas.indexOf('build') === -1) D.stepsWithCanvas.push('build');
    var frame = document.getElementById('vt-canvas-frame');
    var panel = document.createElement('section');
    panel.className = 'vt-step';
    panel.dataset.step = 'build';
    stageEl.insertBefore(panel, frame);
    state.skipWelcome = true;   // no "Let's build your shoe / Begin" page — straight to the shoes
  }

  var betaPulseTimer = null;
  function startBeta() {
    if (!w.VitrineBeta) { console.error('[vitrine] beta module missing'); return; }
    beta = w.VitrineBeta.create({
      D: D, P: P, state: state,
      panel:      stepPanel('build'),
      viewer:     function () { return viewer; },
      liveGroups: function () { return liveGroups; },
      hasScene:   function () { return !!(viewer && viewer.getModelScene && viewer.getModelScene()); },
      goToStep:   goToStep,
      // Colour: a curated colourway (fresh canvas, like the Colors step) or a
      // colour on a set of meshes (stored per mesh, like the parts editor).
      selectColorway: function (cw) {
        var hadMats = Object.keys(state.partMaterialsByMeshName).length > 0;
        state.selectedColorway = cw;
        state.partColors = {}; state.partColorsByMeshName = {};
        state.partMaterials = {}; state.partMaterialsByMeshName = {};
        state.materialMode = 'mixed';
        if (hadMats) { loadedShoeId = null; ensureLiveScene(); }   // swaps live on the materials — reload
        else if (viewer && viewer.getModelScene()) repaintReview();
      },
      paintMeshes: function (meshes, swatch) {
        meshes.forEach(function (m) { if (m.name) state.partColorsByMeshName[m.name] = swatch; });
        if (viewer && viewer.getModelScene()) repaintReview();
      },
      colorways:    function () { return orderedColorwaysForShoe(state.selectedShoe.id); },
      // Fills every <img data-thumb-for-cw="id"> with the current shoe in that
      // colourway (cached per shoe; the Colors step's thumbnail job).
      colorwayThumbs: renderColorwayThumbnails,
      shoeColorway: function (cw) { return shoeColorway(cw, state.selectedShoe.id); },
      isWeltGroup:  isWeltGroup,
      shoeHasWelt:  shoeHasWelt,
      weltOn:       weltOn,
      setWelt:      setWelt,
      liningChoices: liningChoices,
      selectLining: selectLining,
      setSolePosition: setSolePosition,
      soleFeels:    SOLE_FEELS,
      soleSpecsHtml: function (feel, footbed) {
        return soleSpecItemsHtml(feel === 'cloud' ? cloudFeelSpecs() : groundFeelSpecs(footbed));
      },
      soleComboName: soleComboName,
      recChip:      recChip,
      // Camera + "this is the part" pulses.
      focus: function (preset) { if (viewer && viewer.focusView) viewer.focusView(preset, { spin: false }); },
      pulse: function (slug, ms) {
        if (betaPulseTimer) { clearTimeout(betaPulseTimer); betaPulseTimer = null; }
        startStepPulse(slug);
        betaPulseTimer = setTimeout(function () { betaPulseTimer = null; stopStepPulse(); }, ms || 4200);
      },
      flash: function (group) { if (viewer && viewer.flashPart && group) viewer.flashPart(group); },
      // Meshes for the beta's glow: the sole unit (never the welt) and the drawn lining.
      soleMeshes: function () {
        var out = viewer && viewer.getSoleMeshes ? viewer.getSoleMeshes().slice() : [];
        liveGroups.forEach(function (g) { if (g.isSole && !isWeltGroup(g)) out = out.concat(g.meshes); });
        return out;
      },
      liningMeshes: function () { return viewer ? collectLiningMeshes(viewer.getModelScene()) : []; },
      // Pro: every part on its own (Customize's Advanced grouping), per-part material.
      setPartsMode: function (mode) {
        if (state.partsMode === mode) return;
        state.partsMode = mode;
        if (viewer && viewer.getModelScene()) repaintReview();
      },
      applyPartMaterial: applyPartMaterial,
      kindAvailable: function (kind) { return !!kindSwapSource(kind); },
      displayLabel: displayLabel,
      // Put part edits back (Pro → leave without saving). Material swaps live on
      // the loaded materials, so undoing one needs a fresh load.
      restoreOverrides: function (colors, mats) {
        var matsChanged = JSON.stringify(mats) !== JSON.stringify(state.partMaterialsByMeshName);
        state.partColorsByMeshName = colors;
        state.partMaterialsByMeshName = mats;
        state.partColors = {}; state.partMaterials = {};
        if (matsChanged) { loadedShoeId = null; ensureLiveScene(); }
        else if (viewer && viewer.getModelScene()) repaintReview();
      },
      hasProgress: hasProgress,
      allowLeave:  function () { leaveOk = true; },
      rootEl:      rootEl,
      // Cart / share / photos — same code paths as Review.
      addToCart:      addToCart,
      updateCartLine: updateCartLine,
      shareLink:      shareLink,
      openNotesModal: openNotesModal,
      handleTakePhotos: handleTakePhotos,
      refreshCartActions: refreshCartActions,
      editingInCart:  function () { return !!(state.editingLineKey && state.editLineInCart); },
      hasScan:        function () { return !!(w.cfHasScan && w.cfHasScan()); },
      openChat:       openShopifyChat
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STEP TEMPLATES — head & foot scaffolding shared across steps
  // ──────────────────────────────────────────────────────────────────────────
  function stepPanel(slug) { return document.querySelector('.vt-step[data-step="' + slug + '"]'); }

  // One canonical "Recommended" chip, used identically across Materials, Lining
  // and Sole so the affordance reads the same everywhere.
  function recChip() { return '<span class="vt-rec-chip">Recommended</span>'; }

  function stepHead(slug) {
    var step = STEPS.find(function (s) { return s.slug === slug; }) || {};
    return '<header class="vt-step-head">'
         +   '<p class="vt-eyebrow">' + step.eyebrow + '</p>'
         +   '<h1 class="vt-title">' + step.blurb + '</h1>'
         + '</header>';
  }

  function stepFoot(slug, opts) {
    opts = opts || {};
    var backHtml = opts.hideBack ? '<span></span>'
                                 : '<button class="vt-btn-ghost" data-nav="back">← Back</button>';
    var nextLabel = (slug === 'parts') ? 'Review →' : 'Continue →';
    return '<footer class="vt-step-foot">'
         +   backHtml
         +   '<div class="vt-foot-right">'
         +     '<button class="vt-help" data-help>Help</button>'
         +     '<button class="vt-btn vt-btn--next" data-nav="next">'
         +       '<span class="vt-next-label">' + nextLabel + '</span>'
         +     '</button>'
         +   '</div>'
         + '</footer>';
  }

  // Shopify Inbox renders as a custom element <inbox-online-store-chat id="ShopifyChat">.
  // Its launcher button lives in the element's SHADOW DOM (so document.querySelector
  // can't reach it), there's no JS open() API, and the is-open attribute only reflects
  // state outward — setting it does nothing. The one reliable lever is clicking the
  // shadow-DOM toggle button. Default: native widget hidden via CSS. Help button:
  //   1) reveals the widget (vt-chat-open body class)
  //   2) clicks the shadow toggle once the widget has mounted (retries until it does)
  //   3) observes is-open and re-hides the widget when the user closes the chat
  var chatStateObserver = null;

  function chatHost()  { return document.getElementById('ShopifyChat'); }
  function chatIsOpen(host) { return !!host && host.getAttribute('is-open') === 'true'; }

  function chatToggle(host) {
    if (!host || !host.shadowRoot) return null;
    return host.shadowRoot.querySelector('button[data-spec="toggle-button"]')
        || host.shadowRoot.querySelector('.chat-toggle');
  }

  function openShopifyChat() {
    document.body.classList.add('vt-chat-open');

    var deadline = Date.now() + 5000;
    var clicked = false;

    (function attempt() {
      var host = chatHost();
      if (chatIsOpen(host)) { watchChatClose(host); return; }   // open — done

      // Click the launcher exactly once, as soon as it exists in the shadow DOM.
      // Clicking again after it's open would toggle it back closed.
      if (!clicked) {
        var btn = chatToggle(host);
        if (btn) { btn.click(); clicked = true; }
      }

      if (Date.now() < deadline) {
        setTimeout(attempt, 120);            // widget still mounting / not open yet
      } else {
        document.body.classList.remove('vt-chat-open');  // gave up — re-hide
      }
    })();
  }

  // When the user closes the chat, is-open flips off the host. Re-hide the widget
  // so only the Vitrine's own Help button remains.
  function watchChatClose(host) {
    if (chatStateObserver || !host) return;
    chatStateObserver = new MutationObserver(function () {
      if (!chatIsOpen(host)) {
        document.body.classList.remove('vt-chat-open');
        chatStateObserver.disconnect();
        chatStateObserver = null;
      }
    });
    chatStateObserver.observe(host, { attributes: true, attributeFilter: ['is-open'] });
  }

  // Keep the "next" button label in sync. Before Review has ever been reached
  // it's the plain linear flow ("Continue →", and "Review →" on the last step).
  // Once the user has seen Review, every earlier step's button becomes a one-tap
  // "Back to review →" (nextStep jumps straight there).
  function updateFooterLabels() {
    document.querySelectorAll('.vt-btn--next .vt-next-label').forEach(function (el) {
      var step = el.closest('.vt-step').dataset.step;
      if (state.reachedReview)   el.textContent = 'Back to review →';
      else if (step === 'parts') el.textContent = BETA ? 'Done →' : 'Review →';
      else                       el.textContent = 'Continue →';
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window);
